import { useState, useEffect, useRef, useCallback } from "react";
import { usePageTitle } from "@/hooks/usePageTitle";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getOfferings } from '@/services/OfferingServices';
import { OFFERING_NUDGE_COPY, REVIEW_TIME_LABEL, resolvePostSubmitNudge } from '@/lib/offeringNudge';
import { getVideoStatus } from "@/services/introVideoService";
import { IntroVideoUpload } from "@/components/video/IntroVideoUpload";
import Cropper from "react-easy-crop";
import type { Area, Point } from "react-easy-crop";
import { Link, useNavigate } from "react-router-dom";
import {
  User, Camera, GraduationCap, FileText, Video,
  CheckCircle, Plus, X, Upload, Phone, ShieldCheck, ShieldX,
  Trash2, ExternalLink, HelpCircle, Check, ArrowRight,
  ZoomIn, ZoomOut, RotateCcw, RotateCw, Pencil, AlertCircle,
  ChevronLeft, ChevronRight, FileEdit, MapPin, Sparkles,
  Globe, Home, Shuffle, Lightbulb, TrendingUp, Eye, Briefcase, Copy, Share2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from 'sonner';
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { discardDraft, withdrawDraft, saveEducation } from '@/services/teacherProfileService';
import { SearchableMultiSelect } from "@/components/ui/searchable-multi-select";
import { SearchableSelect } from "@/components/ui/searchable-select";
import { RichTextEditor } from "@/components/common/profile-setup/RichTextEditor";
import { useTeacherProfile } from '@/contexts/TeacherProfileContext';
import { DiscardDraftDialog } from "@/components/common/profile-setup/DiscardDraftDialog";
import { ActiveProfileView } from "@/components/common/profile-setup/ActiveProfileView";
import type { VersionData, VersionStatus } from "@/types/teacherProfile";


async function getCroppedImageBlob(imageSrc: string, pixelCrop: Area, rotation: number): Promise<Blob> {
  const image = await new Promise<HTMLImageElement>((res, rej) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => res(img);
    img.onerror = rej;
    img.src = imageSrc;
  });
  const maxSize = Math.max(image.width, image.height);
  const safeArea = 2 * ((maxSize / 2) * Math.sqrt(2));
  const canvas = document.createElement("canvas");
  canvas.width = safeArea;
  canvas.height = safeArea;
  const ctx = canvas.getContext("2d")!;
  ctx.translate(safeArea / 2, safeArea / 2);
  ctx.rotate((rotation * Math.PI) / 180);
  ctx.translate(-safeArea / 2, -safeArea / 2);
  ctx.drawImage(image, safeArea / 2 - image.width / 2, safeArea / 2 - image.height / 2);
  const data = ctx.getImageData(0, 0, safeArea, safeArea);
  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;
  ctx.putImageData(
    data,
    Math.round(0 - safeArea / 2 + image.width / 2 - pixelCrop.x),
    Math.round(0 - safeArea / 2 + image.height / 2 - pixelCrop.y)
  );
  return new Promise((res, rej) => canvas.toBlob(b => b ? res(b) : rej(new Error("toBlob failed")), "image/jpeg", 0.92));
}
import { subjectTopics } from "@/lib/data";
import { cn, formatTeacherLocation } from "@/lib/utils";
import { TeacherSidebar } from "@/components/teacher/dashboard/TeacherSidebar";
import { SidebarProvider } from "@/components/ui/sidebar";
import { DashboardTopNav } from "@/components/layout/DashboardTopNav";
import { saveBasicInfo, saveDescription, uploadProfileImage, submitTeacherProfile, checkHandleAvailability } from '@/services/teacherProfileService';
import { LocationAutocompleteInput, type LocationValue } from '@/components/location/LocationAutocompleteInput';
import { useAuth } from '@/hooks/use-auth-compat';
import { useSwitchRole } from '@/hooks/use-switch-role';
import { fetchSubjects } from "@/services/AdminFetchServices";
import { useExamCatalog } from "@/hooks/useExamCatalog";
import { useCurricula, useLanguages, useGradeBands } from "@/hooks/useValueCatalog";
import { useCurrentUser } from "@/hooks/use-current-user";
import { resolveProfileIdentity } from "@/lib/profileIdentity";
import { FieldHint } from "@/components/help/FieldHint";
import teacherProfileHelp from "@/content/help/teacher-profile-help.json";
import { BioTemplatePicker } from "@/components/teacher/profile/BioTemplatePicker";
import { BIO_PLACEHOLDER_ERROR, hasBioPlaceholders } from "@/content/bioTemplates";

function StatusBadge({ status }: { status: string }) {
  if (status === "APPROVED") return (
    <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">
      <CheckCircle className="w-3 h-3" /> Approved
    </span>
  );
  if (status === "PENDING_REVIEW") return (
    <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400">
      <ShieldCheck className="w-3 h-3" /> Pending Review
    </span>
  );
  if (status === "CHANGES_REQUESTED") return (
    <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">
      <ShieldX className="w-3 h-3" /> Changes Requested
    </span>
  );
  return null;
}


const THRESHOLD_REQUIRED_ACHIEVEMENTS = new Set(['SCORE_ABOVE', 'SCORE_IMPROVEMENT', 'TOP_RANK']);
const ACHIEVEMENT_LABELS: Record<string, string> = {
  SCORE_ABOVE: 'Score Above',
  SCORE_IMPROVEMENT: 'Score Improvement',
  TOP_RANK: 'Top Rank',
};

const TEACHING_APPROACH_OPTIONS = [
  "Curriculum based",
  "Exam focused",
  "Certification prep",
  "Concept based",
  "Fundamentals first",
  "Hands-on learning",
  "Demonstration / Practical based",
  "Project based",
  "Practice questions",
  "Step-by-step guidance",
];

export default function TeacherProfileSetup() {
  usePageTitle('Profile Setup | BookMyCoaching');
  const [editingSection, setEditingSection] = useState<string | null>(null);
  const [videoScript, setVideoScript] = useState<string>("");
  const [photoPreview, setPhotoPreview] = useState<string | null>(null);
  const [cropSrc, setCropSrc] = useState<string | null>(null);
  const [crop, setCrop] = useState<Point>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [cropApplying, setCropApplying] = useState(false);
  const [cropAspect, setCropAspect] = useState<number | undefined>(1);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { teacherProfile, userId, setTeacherProfile, refreshProfile, hasActiveVersion, hasDraft, draftStatus, canEdit, canCreateDraft, sections, profileResponse } = useTeacherProfile();
  // handle lives in VersionData but drafts may have a null handle even when the active version has one.
  // Fall back: draft handle → active version handle → null
  const handle: string | null =
    (teacherProfile as any)?.handle ||
    profileResponse?.profile?.activeVersion?.data?.handle ||
    null;
  const auth = useAuth();
  // Only poll status for a video that's actually been submitted and is still
  // pending server-side processing — not on every page load. (A finished
  // upload done in this session still surfaces here via the shared
  // ['videoStatus'] cache key that useVideoUpload's own query keeps fresh.)
  const introVideoPending = !!teacherProfile?.introVideo &&
    teacherProfile.introVideo.status !== 'READY' &&
    teacherProfile.introVideo.status !== 'FAILED';
  const { data: liveVideoStatus } = useQuery({
    queryKey: ['videoStatus'],
    queryFn: () => getVideoStatus(auth.user?.access_token || sessionStorage.getItem('access_token') || ''),
    enabled: introVideoPending && !!(auth.user?.access_token || sessionStorage.getItem('access_token')),
    refetchOnWindowFocus: false,
  });

  const photoInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [cameraPhotoError, setCameraPhotoError] = useState<string | null>(null);
  const cameraPhotoStreamRef = useRef<MediaStream | null>(null);
  const cameraPhotoPreviewRef = useRef<HTMLVideoElement>(null);
  const isMobile = /Mobi|Android|iPhone|iPad/i.test(navigator.userAgent);

  const [locationValue, setLocationValue] = useState<LocationValue | null>(null);
  const [handleAvailability, setHandleAvailability] = useState<'idle' | 'checking' | 'available' | 'taken' | 'invalid'>('idle');
  const handleCheckTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { user: currentUser } = useCurrentUser();
  // Header identity reflects Settings changes immediately — see resolveProfileIdentity.
  // SCRUM-365: no residence-country chip.
  const { name: identityName, phone: identityPhone } = resolveProfileIdentity({
    account: currentUser,
    versionData: teacherProfile,
  });
  const { ensureRole } = useSwitchRole();
  const [formData, setFormData] = useState({
    handle: "",
    location: "",
    teachingModes: [] as string[],
    subjectCombinations: [{ subject: "", depthLevel: "" }] as { subject: string; depthLevel: string }[],
    // F1 — BASIC_INFO new fields
    teachingAudience: [] as string[],
    curriculumSystems: [] as string[],
    instructionLanguages: [] as string[],
    yearsOfExperience: "",
    bio: "",
    teachingApproach: [] as string[],
    // F2 — DESCRIPTION new fields
    examSuccessStories: [] as Array<{ studentCount: number | ""; achievement: string; threshold: string; subjectExamId: string; timeframe: string }>,
    // F3 — EDUCATION credential fields
    highestQualification: "",
    otherHighestQualification: "",
    highestQualificationInstitution: "",
    teachingQualification: "",
    otherTeachingQualification: "",
    yearsTeachingExperience: "",
    formerSchoolTeacher: false,
    formerInstitutionName: "",
    formerInstitutionYears: "" as number | "",
    profileImage: null as File | null,
  });

  const [subjects, setSubjects] = useState<{ id: string; name: string }[]>([]);
  const { exams } = useExamCatalog();
  const curricula = useCurricula();
  const languages = useLanguages();
  const gradeBands = useGradeBands();
  const [subjectSearch, setSubjectSearch] = useState("");
  const [subjectDropdownOpen, setSubjectDropdownOpen] = useState(false);
  const subjectInputRef = useRef<HTMLInputElement>(null);
  const subjectContainerRef = useRef<HTMLDivElement>(null);

  const sidebarSubjects = Array.from(new Set(
    formData.subjectCombinations
      .map(c => {
        const matched = subjects.find(s => s.id === c.subject || s.name === c.subject);
        return matched?.name;
      })
      .filter(Boolean)
  ));

useEffect(() => {
    fetchSubjects()
      .then(data => { if (data) setSubjects(data); })
      .catch(err => console.error('Failed to fetch subjects', err));
    // Always fetch latest profile on page mount (catches admin approval/rejection)
    refreshProfile(true);
  }, []);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (subjectContainerRef.current && !subjectContainerRef.current.contains(e.target as Node)) {
        setSubjectDropdownOpen(false);
        setSubjectSearch("");
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);
  
  const [validationErrors, setValidationErrors] = useState<{
    handle: boolean;
    teachingModes: boolean;
    subjects: boolean;
    bio: boolean;
    teachingApproach: boolean;
  }>({
    handle: false,
    teachingModes: false,
    subjects: false,
    bio: false,
    teachingApproach: false,
  });
  const [bioPlaceholderError, setBioPlaceholderError] = useState(false);

  // Load profile data into form when available.
  // Skipped while a section is actively being edited — context refetches
  // (polling, role checks) would otherwise clobber the user's in-progress edits.
  useEffect(() => {
    if (editingSection) return;
    console.log('TeacherProfileSetup: teacherProfile changed', teacherProfile);
    if (teacherProfile) {
      // Handle various field name variations for bio
      const bioValue = teacherProfile.professionalBio || "";

      // Deserialize teaching approach: stored as comma-separated string, used as string[].
      // Filter to known options — legacy profiles stored free text here, and its
      // comma-fragments must not appear as selected chips.
      const teachingApproachValue: string[] = teacherProfile.teachingApproach
        ? teacherProfile.teachingApproach.split(",").map((s: string) => s.trim())
            .filter((s: string) => TEACHING_APPROACH_OPTIONS.includes(s))
        : [];

      // Restore location from saved profile data
      if (teacherProfile.placeId && teacherProfile.locationDisplayName) {
        setLocationValue({
          placeId: teacherProfile.placeId,
          displayName: teacherProfile.locationDisplayName,
          lat: teacherProfile.latitude ?? 0,
          lng: teacherProfile.longitude ?? 0,
        });
      }

      const newFormData = {
        handle: handle ?? "",
        location: teacherProfile.locationDisplayName || "",
        teachingModes: teacherProfile.teachingModes || [],
        subjectCombinations: (teacherProfile.subjects || []).map((subject: any) => {
          const subjectId = subject?.subjectId || subject?.subject?.id || subject?.subject || '';
          const expertiseEntry = ((teacherProfile as any).subjectExpertise ?? []).find((se: any) => se.subjectId === subjectId);
          return {
            subject: subjectId,
            depthLevel: expertiseEntry?.depthLevel ?? '',
          };
        }).filter((c: any) => c.subject !== ''),
        yearsOfExperience: "",
        bio: bioValue,
        teachingApproach: teachingApproachValue,
        // F1 — BASIC_INFO new fields
        teachingAudience: (teacherProfile as any).teachingAudience ?? [],
        curriculumSystems: (teacherProfile as any).curriculumSystems ?? [],
        instructionLanguages: (teacherProfile as any).instructionLanguages ?? [],
        // F2 — DESCRIPTION new fields
        examSuccessStories: ((teacherProfile as any).examSuccessStories ?? []).map((s: any) => ({
          studentCount: s.studentCount,
          achievement: s.achievement,
          threshold: s.threshold ?? "",
          subjectExamId: s.subjectExamId ?? "",
          timeframe: s.timeframe,
        })),
        // F3 — EDUCATION credential fields
        highestQualification: (teacherProfile as any).highestQualification ?? "",
        otherHighestQualification: (teacherProfile as any).otherHighestQualification ?? "",
        highestQualificationInstitution: (teacherProfile as any).highestQualificationInstitution ?? "",
        teachingQualification: (teacherProfile as any).teachingQualification ?? "",
        otherTeachingQualification: (teacherProfile as any).otherTeachingQualification ?? "",
        yearsTeachingExperience: (teacherProfile as any).yearsTeachingExperience ?? "",
        formerSchoolTeacher: (teacherProfile as any).formerSchoolTeacher ?? false,
        formerInstitutionName: (teacherProfile as any).formerInstitutionName ?? "",
        formerInstitutionYears: (teacherProfile as any).formerInstitutionYears ?? "",
        profileImage: null as File | null,
      };

      console.log('TeacherProfileSetup: Setting formData with profile data', {
        ...newFormData,
        subjectCombinations: newFormData.subjectCombinations.map(c => `${c.subject} (depth: ${c.depthLevel})`)
      });
      setFormData(newFormData);

    }
  }, [teacherProfile, currentUser, editingSection]);

  const handleFinish = async () => {
    const token = await getToken();
    if (!token) {
      toast.error('Authentication required');
      return;
    }
    setSubmitError(null);
    const toastId = toast.loading('Submitting profile for review...');
    try {
      await submitTeacherProfile(token);
      const nudgePromise = resolvePostSubmitNudge(() =>
        queryClient.fetchQuery({ queryKey: ['teacherOfferings'], queryFn: () => getOfferings(token), staleTime: 0, retry: false }));
      await refreshProfile(true);
      const nudge = await nudgePromise;
      toast.success('Profile submitted for review!', { id: toastId });
      if (nudge === 'SKIP_NUDGE') navigate('/teacher-dashboard'); else setShowNextStepDialog(true);
    } catch (err: any) {
      console.error('Failed to submit profile', err);
      const msg = err?.response?.data?.message || err?.response?.data?.error || err?.message || 'Failed to submit profile for review. Please try again.';
      toast.error(msg, { id: toastId });
      setSubmitError(msg);
    }
  };

  const getToken = () => auth.getToken();

  // Version-aware helpers - teacherProfile is now the current version (draft or active)
  const displayProfile = teacherProfile;

  // Whether the current view is read-only (PENDING_REVIEW draft, or viewing active tab)
  const isDraftReadOnly = draftStatus === 'PENDING_REVIEW';
  const isDraftRejected = draftStatus === 'REJECTED';

  // Get admin feedback for a section from the sections array
  const getAdminComment = (sectionId: string): string | null => {
    const sec = sections.find((s) => s.id === sectionId);
    if (!sec?.adminFeedback?.length) return null;
    return sec.adminFeedback.map(f => `${f.field}: ${f.note || f.reasonCode}`).join('; ');
  };

  /**
   * Creates a draft by re-saving the active version's basic_info.
   * Returns a section id to open instead of the requested one, or null.
   */
  const handleCreateDraft = async (): Promise<string | null> => {
    const token = await getToken();
    if (!token) { toast.error('Authentication required'); throw new Error('Authentication required'); }
    try {
      // Trigger implicit draft creation by saving current basic_info data
      // Backend creates the draft version automatically on the first section PUT
      const activeData = profileResponse?.profile?.activeVersion?.data;
      if (activeData && (!activeData.handle || !activeData.locationDisplayName)) {
        // Older profiles may predate the handle/location requirement. Re-saving
        // them as-is would fail backend validation, so send the teacher to the
        // About section — saving it with the missing fields creates the draft.
        toast.info('Please add your profile handle and location, then save to continue editing.');
        return 'about';
      }
      if (activeData) {
        await saveBasicInfo({
          handle: activeData.handle,
          teachingModes: activeData.teachingModes || [],
          subjects: (activeData.subjects || []).map(s => ({ subjectId: s.subjectId, topicIds: s.topicIds || [] })),
          placeId: activeData.placeId || 'unknown',
          locationDisplayName: activeData.locationDisplayName,
          // lat/lng not stored in frontend state — use 0 to trigger re-geocode on server
          latitude: 0,
          longitude: 0,
        }, token);
      }
      toast.success('You can now edit your profile.');
      await refreshProfile(true);
      return null;
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to create draft';
      toast.error(message);
      throw err;
    }
  };

  const handleDiscardDraft = async () => {
    const token = await getToken();
    if (!token) {
      toast.error('Authentication required');
      return;
    }
    try {
      await discardDraft(token);
      toast.success('Draft discarded. Your active profile is unchanged.');
      setEditingSection(null);
      await refreshProfile(true);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to discard draft';
      toast.error(message);
      throw err;
    }
  };

  const handleWithdrawDraft = async () => {
    const token = await getToken();
    if (!token) { toast.error('Authentication required'); return; }
    try {
      await withdrawDraft(token);
      toast.success('Profile withdrawn from review. You can now edit it.');
      await refreshProfile(true);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Failed to withdraw';
      toast.error(message);
    }
  };

  const countHtmlWords = (html: string) => {
    const tmp = document.createElement("div");
    tmp.innerHTML = html;
    return (tmp.textContent || tmp.innerText || "").trim().split(/\s+/).filter(Boolean).length;
  };

  const HANDLE_PATTERN = /^[a-z0-9-]{3,30}$/;

  const handleSaveAbout = async (): Promise<boolean> => {
    const locationMissing = !locationValue?.placeId;
    const handleInvalid = !HANDLE_PATTERN.test(formData.handle.trim());
    const errors = {
      handle: handleInvalid,
      teachingModes: formData.teachingModes.length === 0,
      subjects: formData.subjectCombinations.filter(c => c.subject).length === 0,
      bio: false,
      teachingApproach: false,
    };
    setValidationErrors(errors);

    if (errors.handle || errors.teachingModes || errors.subjects || locationMissing) {
      const missing = [];
      if (errors.handle) missing.push('Handle (3-30 chars, lowercase letters, digits, hyphens only)');
      if (errors.teachingModes) missing.push('Teaching Mode');
      if (errors.subjects) missing.push('Subjects');
      if (locationMissing) missing.push('Location');
      toast.error(`Please fix: ${missing.join(', ')}`);
      return false;
    }

    if (formData.handle.trim() && handleAvailability === 'taken') {
      toast.error('This handle is already taken. Please choose a different one.');
      return false;
    }
    // Skip API call if nothing changed
    const currentSubjects = JSON.stringify(formData.subjectCombinations.map(c => ({ subjectId: c.subject, depthLevel: c.depthLevel })));
    const savedSubjects = JSON.stringify((teacherProfile?.subjects || []).map((s: any) => {
      const sid = s.subjectId || s.subject?.id;
      const exp = ((teacherProfile as any)?.subjectExpertise ?? []).find((se: any) => se.subjectId === sid);
      return { subjectId: sid, depthLevel: exp?.depthLevel ?? '' };
    }));
    const unchanged = teacherProfile &&
      formData.handle.trim() === (handle || '') &&
      JSON.stringify(formData.teachingModes) === JSON.stringify(teacherProfile.teachingModes || []) &&
      currentSubjects === savedSubjects &&
      locationValue?.placeId === (teacherProfile.placeId || '');

    if (unchanged) {
      setEditingSection(null);
      return true;
    }

    const token = await getToken();
    if (!token) { toast.error('Authentication required'); return false; }
    try {
      // Create the TEACHER role now that the user is submitting their first
      // required section. Until this point no account exists, so abandoning the
      // setup earlier leaves nothing behind. Idempotent for existing teachers.
      //
      // Capture "first role ever" BEFORE calling ensureRole — it triggers an
      // internal refetch() on success, so checking currentUser.roles after
      // the call would always show TEACHER already present. Only a genuine
      // first-ever registration gets the coin bonus welcome toast (ADR 074).
      const wasFirstRoleEver = (currentUser?.roles?.length ?? 0) === 0;
      const ready = await ensureRole('TEACHER');
      if (!ready) return false;
      if (wasFirstRoleEver) {
        sessionStorage.setItem('teacher_coin_bonus_welcome', 'true');
      }
      await saveBasicInfo({
        handle: formData.handle.trim(),
        teachingModes: formData.teachingModes,
        subjects: formData.subjectCombinations.map(c => ({ subjectId: c.subject, topicIds: [] })),
        placeId: locationValue!.placeId,
        locationDisplayName: locationValue!.displayName,
        latitude: locationValue!.lat,
        longitude: locationValue!.lng,
        teachingAudience: formData.teachingAudience.length > 0 ? formData.teachingAudience : undefined,
        curriculumSystems: formData.curriculumSystems.length > 0 ? formData.curriculumSystems : undefined,
        instructionLanguages: formData.instructionLanguages.length > 0 ? formData.instructionLanguages : undefined,
        subjectExpertise: formData.subjectCombinations
          .filter(c => c.subject && c.depthLevel)
          .map(c => ({ subjectId: c.subject, depthLevel: c.depthLevel })),
      }, token);
      toast.success('Changes saved');
      setEditingSection(null);
      setValidationErrors({ handle: false, teachingModes: false, subjects: false, bio: false, teachingApproach: false });
      // Awaited: the data-load effect below (keyed on teacherProfile) overwrites the
      // whole formData object once this resolves, guarded only by editingSection
      // (just cleared above). Firing this without awaiting let the caller advance
      // to a later wizard step before it resolved, so it would land after the user
      // had already entered new data there and silently wipe it.
      await refreshProfile(true).catch(() => {});
      return true;
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.response?.data?.error || err?.message || 'Failed to save basic info';
      toast.error(msg);
      return false;
    }
  };

  const handleSaveDescription = async (): Promise<boolean> => {
    const bioWordCount = countHtmlWords(formData.bio);

    const errors = {
      handle: false,
      teachingModes: false,
      subjects: false,
      bio: bioWordCount < 50,
      teachingApproach: formData.teachingApproach.length === 0,
    };
    setValidationErrors(errors);
    const bioHasPlaceholders = hasBioPlaceholders(formData.bio);
    setBioPlaceholderError(bioHasPlaceholders);

    if (errors.bio || errors.teachingApproach || bioHasPlaceholders) {
      return false;
    }

    // Validate threshold for achievements that require it
    const missingThreshold = formData.examSuccessStories
      .filter(s => s.achievement && s.timeframe && s.studentCount !== "")
      .find(s => THRESHOLD_REQUIRED_ACHIEVEMENTS.has(s.achievement) && !s.threshold);
    if (missingThreshold) {
      const label = ACHIEVEMENT_LABELS[missingThreshold.achievement] ?? missingThreshold.achievement;
      toast.error(`Threshold is required for '${label}' achievement`);
      return false;
    }

    // Skip API call if nothing changed.
    // Normalize threshold to "" for achievements that don't require it so that
    // a stale threshold value left over from a previous achievement selection
    // doesn't count as a change.
    const serializedApproach = formData.teachingApproach.join(",");
    const normalizeThreshold = (achievement: string, threshold: string | null | undefined) =>
      THRESHOLD_REQUIRED_ACHIEVEMENTS.has(achievement) ? (threshold || "") : "";
    const formStories = formData.examSuccessStories
      .filter(s => s.achievement && s.timeframe && s.studentCount !== "")
      .map(s => ({ studentCount: Number(s.studentCount), achievement: s.achievement, threshold: normalizeThreshold(s.achievement, s.threshold), subjectExamId: s.subjectExamId || "", timeframe: s.timeframe }));
    const savedStories = ((teacherProfile as any)?.examSuccessStories ?? [])
      .filter((s: any) => s.achievement && s.timeframe && s.studentCount !== "")
      .map((s: any) => ({ studentCount: Number(s.studentCount), achievement: s.achievement, threshold: normalizeThreshold(s.achievement, s.threshold), subjectExamId: s.subjectExamId ?? "", timeframe: s.timeframe }));
    const unchanged = teacherProfile &&
      formData.bio === (teacherProfile.professionalBio || '') &&
      serializedApproach === (teacherProfile.teachingApproach || '') &&
      JSON.stringify(formStories) === JSON.stringify(savedStories);

    if (unchanged) {
      setEditingSection(null);
      return true;
    }

    const token = await getToken();
    if (!token) { toast.error('Authentication required'); return false; }
    try {
      await saveDescription({
        professionalBio: formData.bio,
        teachingApproach: formData.teachingApproach.join(","),
        examSuccessStories: formData.examSuccessStories
          .filter(s => s.achievement && s.timeframe && s.studentCount !== "")
          .map(s => ({
            studentCount: Number(s.studentCount),
            achievement: s.achievement,
            threshold: THRESHOLD_REQUIRED_ACHIEVEMENTS.has(s.achievement) ? (s.threshold || undefined) : undefined,
            subjectExamId: s.subjectExamId || undefined,
            timeframe: s.timeframe,
          })),
      }, token);
      toast.success('Changes saved');
      setEditingSection(null);
      setValidationErrors({ handle: false, teachingModes: false, subjects: false, bio: false, teachingApproach: false });
      // Awaited — see handleSaveAbout's identical comment above.
      await refreshProfile(true).catch(() => {});
      return true;
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.response?.data?.error || err?.message || 'Failed to save description';
      toast.error(msg);
      return false;
    }
  };

  const handlePhotoClick = () => {
    photoInputRef.current?.click();
  };

  const handleOpenCamera = async () => {
    if (isMobile) {
      cameraInputRef.current?.click();
      return;
    }
    setCameraPhotoError(null);
    setIsCameraOpen(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "user" }, audio: false });
      cameraPhotoStreamRef.current = stream;
      // attach after dialog renders
      setTimeout(() => {
        if (cameraPhotoPreviewRef.current) {
          cameraPhotoPreviewRef.current.srcObject = stream;
          cameraPhotoPreviewRef.current.play();
        }
      }, 100);
    } catch {
      setCameraPhotoError("Could not access camera. Please check your browser permissions.");
    }
  };

  const stopCameraPhotoStream = () => {
    cameraPhotoStreamRef.current?.getTracks().forEach(t => t.stop());
    cameraPhotoStreamRef.current = null;
  };

  const handleTakeSnapshot = () => {
    const video = cameraPhotoPreviewRef.current;
    if (!video) return;
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext("2d")!.drawImage(video, 0, 0);
    stopCameraPhotoStream();
    setIsCameraOpen(false);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setRotation(0);
    setCroppedAreaPixels(null);
    setCropAspect(1);
    setCropSrc(canvas.toDataURL("image/jpeg", 0.92));
  };

  const handleCloseCameraModal = () => {
    stopCameraPhotoStream();
    setIsCameraOpen(false);
    setCameraPhotoError(null);
  };

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';

    const allowedExtensions = ['jpg', 'jpeg', 'png', 'gif'];
    const fileExtension = file.name.split('.').pop()?.toLowerCase();
    if (!fileExtension || !allowedExtensions.includes(fileExtension)) {
      toast.error('Please select an image file (JPG, JPEG, PNG, GIF)');
      return;
    }
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Please select an image file (JPG, JPEG, PNG, GIF)');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }

    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setRotation(0);
    setCroppedAreaPixels(null);
    setCropAspect(1);
    setCropSrc(URL.createObjectURL(file));
  };

  const onCropCompleteCallback = useCallback((_: Area, pixels: Area) => {
    setCroppedAreaPixels(pixels);
  }, []);

  const handleApplyCrop = async () => {
    if (!croppedAreaPixels || !cropSrc) return;
    setCropApplying(true);
    const toastId = toast.loading('Uploading photo...');
    try {
      const blob = await getCroppedImageBlob(cropSrc, croppedAreaPixels, rotation);
      const token = await getToken();
      if (!token) {
        toast.error('Authentication required to upload photo', { id: toastId });
        return;
      }
      const previewUrl = URL.createObjectURL(blob);
      setPhotoPreview(previewUrl);
      setCropSrc(null);
      const croppedFile = new File([blob], 'profile.jpg', { type: 'image/jpeg' });
      await uploadProfileImage(croppedFile, token);

      // Awaited — same race as handleSaveAbout/handleSaveDescription: advancing to
      // a later wizard step before this resolves lets it land after newer data and
      // silently wipe it.
      await refreshProfile(true).catch(() => {});
      toast.success('Profile photo uploaded successfully', { id: toastId });
      setFormData(prev => ({ ...prev, profileImage: croppedFile }));
    } catch (err: any) {
      console.error('Failed to upload photo', err);
      const msg = err?.response?.data?.message || err?.response?.data?.error || err?.message || 'Failed to upload photo';
      toast.error(msg, { id: toastId });
    } finally {
      setCropApplying(false);
    }
  };

  /* ─── Step content renderers ─── */

  const renderAbout = () => (
    <div className="space-y-7">

      {/* ── Identity ── */}
      <div className="space-y-5">

        {/* Profile Handle — Full Name is now captured at registration and edited in Settings */}
        <div className="grid grid-cols-1 gap-4">

          {/* Profile Handle */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Profile Handle <span className="text-destructive">*</span> <FieldHint content={teacherProfileHelp} stepId="BASIC_INFO" fieldKey="handle" /></Label>
          <div className="relative">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground select-none">@</span>
            <Input
              value={formData.handle}
              onChange={e => {
                const val = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '');
                setFormData(prev => ({ ...prev, handle: val }));
                setHandleAvailability('idle');
                if (validationErrors.handle) setValidationErrors(prev => ({ ...prev, handle: false }));
              }}
              onBlur={async () => {
                const h = formData.handle.trim();
                if (!h) { setHandleAvailability('idle'); return; }
                if (!/^[a-z0-9-]{3,30}$/.test(h)) { setHandleAvailability('invalid'); return; }
                const token = await getToken();
                if (!token) return;
                setHandleAvailability('checking');
                if (handleCheckTimerRef.current) clearTimeout(handleCheckTimerRef.current);
                handleCheckTimerRef.current = setTimeout(async () => {
                  try {
                    const result = await checkHandleAvailability(h, token);
                    const existingHandle = (teacherProfile as any)?.handle;
                    if (!result.available && h !== existingHandle) {
                      setHandleAvailability('taken');
                    } else {
                      setHandleAvailability('available');
                    }
                  } catch {
                    setHandleAvailability('idle');
                  }
                }, 300);
              }}
              placeholder="yourname"
              maxLength={30}
              className={cn("h-11 pl-8", validationErrors.handle && "border-destructive focus-visible:ring-destructive")}
            />
          </div>
          <p className="text-xs text-muted-foreground">3-30 chars: lowercase, digits, hyphens</p>
          {validationErrors.handle && <p className="text-xs text-destructive">Invalid handle format</p>}
          {handleAvailability === 'checking' && <p className="text-xs text-muted-foreground">Checking availability...</p>}
          {handleAvailability === 'available' && <p className="text-xs text-green-600 dark:text-green-400">Handle is available</p>}
          {handleAvailability === 'taken' && <p className="text-xs text-destructive">This handle is already taken</p>}
          {handleAvailability === 'invalid' && <p className="text-xs text-destructive">Handle must be 3-30 characters: lowercase letters, digits, and hyphens only</p>}
        </div>

        </div>{/* end Handle grid */}

        {/* Location — Phone is now captured at registration and edited in Settings */}
        <div className="grid grid-cols-1 gap-4">

          {/* Location */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Location <span className="text-destructive">*</span></Label>
            <LocationAutocompleteInput
              value={locationValue}
              onChange={(loc) => {
                setLocationValue(loc);
                setFormData(prev => ({ ...prev, location: loc?.displayName || "" }));
              }}
              placeholder="Your city or area"
            />
            {!locationValue && (
              <p className="text-xs text-muted-foreground">Only city/area is shown publicly.</p>
            )}
          </div>

        </div>{/* end Phone + Location grid */}

      </div>{/* end Identity section */}

      {/* ── What you teach ── */}
      <div className="space-y-5">

      {/* Subjects — modern tag combobox */}
      {(() => {
        const subjectColors = [
          { bg: "bg-orange-100", text: "text-orange-700", border: "border-orange-200", dot: "bg-orange-400", hover: "hover:bg-orange-200" },
          { bg: "bg-violet-100", text: "text-violet-700", border: "border-violet-200", dot: "bg-violet-400", hover: "hover:bg-violet-200" },
          { bg: "bg-amber-100",   text: "text-amber-700",   border: "border-amber-200",   dot: "bg-amber-400",   hover: "hover:bg-amber-200"   },
          { bg: "bg-rose-100",   text: "text-rose-700",   border: "border-rose-200",   dot: "bg-rose-400",   hover: "hover:bg-rose-200"   },
          { bg: "bg-indigo-100",   text: "text-indigo-700",   border: "border-indigo-200",   dot: "bg-indigo-400",   hover: "hover:bg-indigo-200"   },
          { bg: "bg-amber-100",  text: "text-amber-700",  border: "border-amber-200",  dot: "bg-amber-400",  hover: "hover:bg-amber-200"  },
          { bg: "bg-emerald-100",text: "text-emerald-700",border: "border-emerald-200",dot: "bg-emerald-400",hover: "hover:bg-emerald-200"},
          { bg: "bg-pink-100",   text: "text-pink-700",   border: "border-pink-200",   dot: "bg-pink-400",   hover: "hover:bg-pink-200"   },
        ];
        const selectedSubjects = formData.subjectCombinations.filter(c => c?.subject && typeof c.subject === 'string');
        const availableSubjects = subjects.filter(s => !formData.subjectCombinations.some(c => c?.subject === s.id));
        const filteredSubjects = availableSubjects.filter(s => s.name.toLowerCase().includes(subjectSearch.toLowerCase()));

        const addSubject = (subjectId: string) => {
          const emptyIdx = formData.subjectCombinations.findIndex(c => !c.subject);
          if (emptyIdx >= 0) {
            setFormData(prev => ({
              ...prev,
              subjectCombinations: prev.subjectCombinations.map((c, i) =>
                i === emptyIdx ? { subject: subjectId, depthLevel: "" } : c
              ),
            }));
          } else {
            setFormData(prev => ({
              ...prev,
              subjectCombinations: [...prev.subjectCombinations, { subject: subjectId, depthLevel: "" }],
            }));
          }
          setValidationErrors(prev => ({ ...prev, subjects: false }));
          setSubjectSearch("");
          subjectInputRef.current?.focus();
        };

        const removeSubject = (idx: number) => {
          setFormData(prev => ({
            ...prev,
            subjectCombinations: prev.subjectCombinations.length > 1
              ? prev.subjectCombinations.filter((_, i) => i !== idx)
              : [{ subject: "", depthLevel: "" }],
          }));
        };

        return (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-sm font-medium">Subjects You Teach <span className="text-destructive">*</span> <FieldHint content={teacherProfileHelp} stepId="BASIC_INFO" fieldKey="subjects" /></Label>
              {selectedSubjects.length > 0 && (
                <span className="text-xs text-muted-foreground">{selectedSubjects.length} selected</span>
              )}
            </div>

            <div ref={subjectContainerRef} className="relative">
              {/* Tag input container */}
              <div
                className={cn(
                  "min-h-[44px] w-full flex flex-wrap gap-2 items-center px-3 py-2 rounded-xl border-2 transition-all duration-200 cursor-text",
                  subjectDropdownOpen
                    ? "border-[hsl(210,60%,28%)] bg-white shadow-sm ring-2 ring-[hsl(210,60%,28%)]/10"
                    : validationErrors.subjects
                    ? "border-destructive bg-muted/60"
                    : "border-transparent bg-muted/60 hover:bg-muted"
                )}
                onClick={() => {
                  setSubjectDropdownOpen(true);
                  subjectInputRef.current?.focus();
                }}
              >
                {/* Selected subject pills */}
                {selectedSubjects.map((combo, idx) => {
                  const subjectId = combo?.subject;
                  const subjectObj = subjects.find(s => s.id === subjectId);
                  const displayName = subjectObj?.name || subjectId;
                  const c = subjectColors[idx % subjectColors.length];
                  return (
                    <span
                      key={idx}
                      className={cn(
                        "inline-flex items-center gap-1.5 pl-2.5 pr-1.5 py-1 rounded-lg border text-xs font-semibold shrink-0",
                        c.bg, c.text, c.border
                      )}
                    >
                      <span className={cn("w-1.5 h-1.5 rounded-full shrink-0", c.dot)} />
                      {displayName}
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); removeSubject(idx); }}
                        className={cn("ml-0.5 p-0.5 rounded transition-colors", c.hover)}
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  );
                })}

                {/* Search input */}
                <input
                  ref={subjectInputRef}
                  value={subjectSearch}
                  onChange={e => { setSubjectSearch(e.target.value); setSubjectDropdownOpen(true); }}
                  onFocus={() => setSubjectDropdownOpen(true)}
                  placeholder={selectedSubjects.length === 0 ? "Search and add subjects…" : "Add more…"}
                  className="flex-1 min-w-[120px] bg-transparent outline-none text-sm placeholder:text-slate-400 text-slate-700"
                />
              </div>

              {/* Dropdown */}
              {subjectDropdownOpen && (
                <div className="absolute z-50 mt-1.5 w-full rounded-xl border border-slate-200 bg-white shadow-lg overflow-hidden animate-in fade-in-0 zoom-in-95 duration-100">
                  <div className="max-h-56 overflow-y-auto py-1.5">
                    {filteredSubjects.length === 0 ? (
                      <div className="px-4 py-6 text-center">
                        <p className="text-sm text-slate-400">
                          {subjectSearch ? `No results for "${subjectSearch}"` : "All subjects added"}
                        </p>
                        {subjectSearch && (
                          <button
                            type="button"
                            onClick={() => { toast("We'll review your request and add it shortly. Thank you!"); setSubjectDropdownOpen(false); setSubjectSearch(""); }}
                            className="mt-2 text-xs text-primary font-medium hover:underline"
                          >
                            Request "{subjectSearch}" be added
                          </button>
                        )}
                      </div>
                    ) : (
                      filteredSubjects.map((s, i) => {
                        const c = subjectColors[i % subjectColors.length];
                        return (
                          <button
                            key={s.id}
                            type="button"
                            onMouseDown={e => e.preventDefault()}
                            onClick={() => addSubject(s.id)}
                            className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors text-left"
                          >
                            <span className={cn("w-2 h-2 rounded-full shrink-0", c.dot)} />
                            {s.name}
                          </button>
                        );
                      })
                    )}
                  </div>
                  <div className="border-t border-slate-100 px-4 py-2 flex items-center gap-1.5">
                    <HelpCircle className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                    <p className="text-xs text-slate-400">
                      Can't find yours?{" "}
                      <button
                        type="button"
                        onClick={() => { toast("We'll review your request and add it shortly. Thank you!"); setSubjectDropdownOpen(false); setSubjectSearch(""); }}
                        className="text-primary font-medium hover:underline"
                      >
                        Request to add it
                      </button>
                    </p>
                  </div>
                </div>
              )}
            </div>

            {validationErrors.subjects && <p className="text-xs text-destructive">Please add at least one subject</p>}
          </div>
        );
      })()}

      {/* Depth level per subject */}
      {formData.subjectCombinations.filter(c => c.subject).length > 0 && (() => {
        const subjectColors = [
          { bg: "bg-orange-100", text: "text-orange-700", border: "border-orange-200", dot: "bg-orange-400" },
          { bg: "bg-violet-100", text: "text-violet-700", border: "border-violet-200", dot: "bg-violet-400" },
          { bg: "bg-amber-100",   text: "text-amber-700",   border: "border-amber-200",   dot: "bg-amber-400"   },
          { bg: "bg-rose-100",   text: "text-rose-700",   border: "border-rose-200",   dot: "bg-rose-400"   },
          { bg: "bg-indigo-100",   text: "text-indigo-700",   border: "border-indigo-200",   dot: "bg-indigo-400"   },
          { bg: "bg-amber-100",  text: "text-amber-700",  border: "border-amber-200",  dot: "bg-amber-400"  },
          { bg: "bg-emerald-100",text: "text-emerald-700",border: "border-emerald-200",dot: "bg-emerald-400"},
          { bg: "bg-pink-100",   text: "text-pink-700",   border: "border-pink-200",   dot: "bg-pink-400"   },
        ];
        return (
          <div className="space-y-2">
            <Label className="text-sm font-medium">Your depth of expertise</Label>
            <p className="text-xs text-muted-foreground">How deeply do you teach each subject? (optional)</p>
            <div className="space-y-2">
              {formData.subjectCombinations.filter(c => c.subject).map((combo, idx) => {
                const subjectObj = subjects.find(s => s.id === combo.subject);
                const displayName = subjectObj?.name || combo.subject;
                const c = subjectColors[idx % subjectColors.length];
                return (
                  <div key={combo.subject} className="flex items-center gap-3">
                    <span className={cn(
                      "inline-flex items-center gap-1.5 pl-2.5 pr-3 py-1.5 rounded-lg border text-xs font-semibold min-w-[130px]",
                      c.bg, c.text, c.border
                    )}>
                      <span className={cn("w-1.5 h-1.5 rounded-full shrink-0", c.dot)} />
                      {displayName}
                    </span>
                    <select
                      value={combo.depthLevel}
                      onChange={e => {
                        const realIdx = formData.subjectCombinations.findIndex(x => x.subject === combo.subject);
                        setFormData(prev => ({
                          ...prev,
                          subjectCombinations: prev.subjectCombinations.map((x, i) =>
                            i === realIdx ? { ...x, depthLevel: e.target.value } : x
                          ),
                        }));
                      }}
                      className="h-9 border rounded-md px-3 text-sm bg-background flex-1 max-w-[210px]"
                    >
                      <option value="">Select depth level…</option>
                      {[
                        { value: "FOUNDATIONAL", label: "Foundational" },
                        { value: "INTERMEDIATE", label: "Intermediate" },
                        { value: "ADVANCED", label: "Advanced" },
                        { value: "UNIVERSITY", label: "University" },
                        { value: "COMPETITIVE", label: "Competitive Exam" },
                      ].map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
                    </select>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      </div>{/* end What you teach */}

      {/* ── How you teach ── */}
      <div className="space-y-5">

      {/* Teaching Mode */}
      <div className="space-y-2">
        <Label className="text-sm font-medium">Teaching Mode <span className="text-destructive">*</span> <FieldHint content={teacherProfileHelp} stepId="BASIC_INFO" fieldKey="teachingModes" /></Label>
        <div className="flex flex-wrap gap-2">
          {[
            {
              value: "ONLINE",
              label: "Online",
              icon: Globe,
              idleBg: "bg-amber-50",
              idleText: "text-amber-600",
              idleBorder: "border-amber-200",
              activeBg: "bg-amber-500",
              activeBorder: "border-amber-500",
              activeText: "text-white",
              activeIcon: "text-white",
            },
            {
              value: "OFFLINE",
              label: "Offline",
              icon: Home,
              idleBg: "bg-violet-50",
              idleText: "text-violet-600",
              idleBorder: "border-violet-200",
              activeBg: "bg-violet-500",
              activeBorder: "border-violet-500",
              activeText: "text-white",
              activeIcon: "text-white",
            },
            {
              value: "HYBRID",
              label: "Hybrid",
              icon: Shuffle,
              idleBg: "bg-amber-50",
              idleText: "text-amber-600",
              idleBorder: "border-amber-200",
              activeBg: "bg-amber-500",
              activeBorder: "border-amber-500",
              activeText: "text-white",
              activeIcon: "text-white",
            },
          ].map((mode) => {
            const selected = formData.teachingModes.includes(mode.value);
            return (
              <button
                key={mode.value}
                type="button"
                onClick={() => {
                  const modes = selected
                    ? formData.teachingModes.filter(m => m !== mode.value)
                    : [...formData.teachingModes, mode.value];
                  setFormData({ ...formData, teachingModes: modes });
                  if (modes.length > 0) setValidationErrors(prev => ({ ...prev, teachingModes: false }));
                }}
                className={cn(
                  "relative inline-flex items-center gap-2 px-3.5 py-2 rounded-lg border-2 text-sm font-semibold transition-all duration-150 cursor-pointer select-none",
                  selected
                    ? cn(mode.activeBg, mode.activeBorder, "shadow-sm scale-[1.02]")
                    : cn(mode.idleBg, mode.idleBorder, mode.idleText, "hover:brightness-95")
                )}
              >
                <mode.icon className={cn("w-3.5 h-3.5 shrink-0", selected ? mode.activeIcon : mode.idleText)} />
                <span className={selected ? mode.activeText : mode.idleText}>{mode.label}</span>
                {selected && <Check className="w-3 h-3 text-white/80 shrink-0" />}
              </button>
            );
          })}
        </div>
        {validationErrors.teachingModes && <p className="text-xs text-destructive">Please select at least one teaching mode</p>}
      </div>

      {/* Teaching Audience */}
      <div className="space-y-2">
        <Label className="text-sm font-medium">Select all grade levels / audiences you teach <FieldHint content={teacherProfileHelp} stepId="BASIC_INFO" fieldKey="teachingAudience" /></Label>
        {gradeBands.isError ? (
          <p role="alert" className="text-sm text-destructive">Couldn't load options. Retrying…</p>
        ) : (
          <SearchableMultiSelect
            options={gradeBands.entries.map(entry => ({ label: entry.label, value: entry.code }))}
            selected={formData.teachingAudience}
            onChange={(next) => setFormData(prev => ({ ...prev, teachingAudience: next }))}
            placeholder="Search and select grade levels…"
            searchPlaceholder="Search grade levels…"
          />
        )}
      </div>

      {/* Curriculum Systems */}
      <div className="space-y-2">
        <Label className="text-sm font-medium">Select all curricula you teach <FieldHint content={teacherProfileHelp} stepId="BASIC_INFO" fieldKey="curriculumSystems" /></Label>
        {curricula.isError ? (
          <p role="alert" className="text-sm text-destructive">Couldn't load options. Retrying…</p>
        ) : (
          <SearchableMultiSelect
            options={curricula.entries.map(entry => ({ label: entry.label, value: entry.code }))}
            selected={formData.curriculumSystems}
            onChange={(next) => setFormData(prev => ({ ...prev, curriculumSystems: next }))}
            placeholder="Search and select curricula…"
            searchPlaceholder="Search curricula…"
          />
        )}
      </div>

      {/* Instruction Languages */}
      <div className="space-y-2">
        <Label className="text-sm font-medium">Languages you teach in <FieldHint content={teacherProfileHelp} stepId="BASIC_INFO" fieldKey="languages" /></Label>
        {languages.isError ? (
          <p role="alert" className="text-sm text-destructive">Couldn't load options. Retrying…</p>
        ) : (
          <SearchableMultiSelect
            options={languages.entries.map(entry => ({ label: entry.label, value: entry.code }))}
            selected={formData.instructionLanguages}
            onChange={(next) => setFormData(prev => ({ ...prev, instructionLanguages: next }))}
            placeholder="Search and select languages…"
            searchPlaceholder="Search languages…"
          />
        )}
      </div>

      </div>{/* end How you teach */}

    </div>
  );

  const firstSubjectId = formData.subjectCombinations.find(c => c.subject)?.subject;
  const bioFacts = {
    name: identityName ?? undefined,
    subject: subjects.find(s => s.id === firstSubjectId || s.name === firstSubjectId)?.name,
    curricula: formData.curriculumSystems
      .map(code => curricula.entries.find(e => e.code === code)?.label ?? '')
      .filter(Boolean),
    qualification: formData.highestQualification,
    otherQualification: formData.otherHighestQualification,
    institution: formData.highestQualificationInstitution,
    experience: formData.yearsTeachingExperience,
  };

  const renderDescription = () => (
    <div className="space-y-8">

      {/* Professional Bio */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Label className="text-sm font-semibold text-foreground">Professional Bio <span className="text-destructive">*</span></Label>
            <BioTemplatePicker
              currentBio={formData.bio}
              facts={bioFacts}
              onApply={(html) => {
                // RichTextEditor syncs external value changes into the editor, so no key bump is needed.
                setFormData(prev => ({ ...prev, bio: html }));
                // Every template is >= 50 words; placeholder errors re-surface on save.
                setValidationErrors(prev => ({ ...prev, bio: false }));
                setBioPlaceholderError(false);
              }}
            />
          </div>
          <span className="text-xs text-muted-foreground">min. 50 words</span>
        </div>
        <RichTextEditor
          key={`bio-${teacherProfile ? 'loaded' : 'empty'}`}
          value={formData.bio}
          onChange={(val) => {
            setFormData(prev => ({ ...prev, bio: val }));
            if (validationErrors.bio) {
              const wc = val.replace(/<[^>]*>/g, " ").trim().split(/\s+/).filter(Boolean).length;
              if (wc >= 50) setValidationErrors(prev => ({ ...prev, bio: false }));
            }
            if (bioPlaceholderError && !hasBioPlaceholders(val)) setBioPlaceholderError(false);
          }}
          placeholder="Share your background, experience, and what makes your teaching style unique..."
          minWords={50}
          error={validationErrors.bio || bioPlaceholderError}
          placeholderJump
        />
        {bioPlaceholderError && (
          <p className="text-xs text-destructive">{BIO_PLACEHOLDER_ERROR}</p>
        )}
        <p className="text-xs text-muted-foreground flex items-center gap-1.5">
          <Lightbulb className="w-3 h-3 text-amber-500 shrink-0" />
          Mention your experience, specialties, and achievements. Be personable and use formatting to keep it easy to scan.
        </p>
      </div>

      {/* Teaching Approach */}
      <div className="space-y-3">
        <div>
          <Label className="text-sm font-semibold text-foreground">Teaching Approach <span className="text-destructive">*</span> <FieldHint content={teacherProfileHelp} stepId="DESCRIPTION" fieldKey="teachingApproach" /></Label>
          <p className="text-xs text-muted-foreground mt-0.5">Select all that apply — students use these to find the right fit.</p>
        </div>
        <SearchableMultiSelect
          options={TEACHING_APPROACH_OPTIONS}
          selected={formData.teachingApproach}
          onChange={(next) => {
            setFormData(prev => ({ ...prev, teachingApproach: next }));
            if (validationErrors.teachingApproach)
              setValidationErrors(prev => ({ ...prev, teachingApproach: false }));
          }}
          placeholder="Search and select teaching approaches…"
          searchPlaceholder="Search approaches…"
        />
        {validationErrors.teachingApproach && (
          <p className="text-xs text-destructive">Please select at least one teaching approach.</p>
        )}
      </div>

      {/* Exam Success Stories */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <Label className="text-sm font-semibold text-foreground">Exam Success Stories <FieldHint content={teacherProfileHelp} stepId="DESCRIPTION" fieldKey="examSuccessStories" /></Label>
            <p className="text-xs text-muted-foreground mt-0.5">Showcase student achievements (up to 5)</p>
          </div>
          {formData.examSuccessStories.length < 5 && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setFormData(prev => ({
                ...prev,
                examSuccessStories: [...prev.examSuccessStories, { studentCount: "", achievement: "", threshold: "", subjectExamId: "", timeframe: "" }],
              }))}
            >
              <Plus className="w-3.5 h-3.5 mr-1" /> Add Story
            </Button>
          )}
        </div>
        {formData.examSuccessStories.map((story, i) => {
          const examName = exams.find(ex => ex.id === story.subjectExamId)?.name ?? "";
          const timeframeLabel: Record<string, string> = {
            LAST_1_YEAR: "in the last 1 year",
            LAST_2_YEARS: "in the last 2 years",
            LAST_3_YEARS: "in the last 3 years",
            LAST_5_YEARS: "in the last 5 years",
            ALL_TIME: "all time",
          };
          const tf = story.timeframe ? timeframeLabel[story.timeframe] ?? "" : "";
          const count = story.studentCount !== "" ? story.studentCount : null;
          const subject = examName ? ` in ${examName}` : "";

          let previewSentence = "";
          if (count && story.achievement) {
            const n = `${count} student${Number(count) !== 1 ? "s" : ""}`;
            if (story.achievement === "SCORE_ABOVE")
              previewSentence = `${n} scored above ${story.threshold || "…"}${subject}${tf ? ", " + tf : ""}`;
            else if (story.achievement === "SCORE_IMPROVEMENT")
              previewSentence = `${n} improved their score by ${story.threshold || "…"}${subject}${tf ? ", " + tf : ""}`;
            else if (story.achievement === "TOP_RANK")
              previewSentence = `${n} achieved a top ${story.threshold || "…"} rank${subject}${tf ? ", " + tf : ""}`;
            else if (story.achievement === "PASS_RATE")
              previewSentence = `${n} passed${subject}${tf ? ", " + tf : ""}`;
            else if (story.achievement === "SELECTION")
              previewSentence = `${n} were selected${subject}${tf ? ", " + tf : ""}`;
          }

          return (
          <div key={i} className="space-y-3 rounded-xl border border-border/60 bg-muted/20 p-4 last:mb-0">
            <div className="flex items-center justify-between">
              <span className="text-xs font-medium text-muted-foreground">Story {i + 1}</span>
              <button
                type="button"
                onClick={() => setFormData(prev => ({
                  ...prev,
                  examSuccessStories: prev.examSuccessStories.filter((_, j) => j !== i),
                }))}
                className="text-xs text-muted-foreground hover:text-destructive cursor-pointer transition-colors flex items-center gap-1"
              >
                <X className="w-3.5 h-3.5" /> Remove
              </button>
            </div>
            <div className={`grid gap-3 ${["SCORE_ABOVE", "SCORE_IMPROVEMENT", "TOP_RANK"].includes(story.achievement) ? "grid-cols-3" : "grid-cols-2"}`}>
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">Students helped</Label>
                <Input
                  type="number"
                  min={1}
                  max={500}
                  value={story.studentCount}
                  onChange={e => setFormData(prev => ({
                    ...prev,
                    examSuccessStories: prev.examSuccessStories.map((x, j) => j === i ? { ...x, studentCount: Number(e.target.value) } : x),
                  }))}
                  className="h-9"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">Achievement</Label>
                <select
                  value={story.achievement}
                  onChange={e => setFormData(prev => ({
                    ...prev,
                    examSuccessStories: prev.examSuccessStories.map((x, j) => j === i ? { ...x, achievement: e.target.value } : x),
                  }))}
                  className="w-full h-9 rounded-md border border-transparent bg-muted/60 px-3 text-sm transition-colors focus:bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                >
                  <option value="">Select…</option>
                  {[
                    { value: "SCORE_ABOVE", label: "Score Above" },
                    { value: "SCORE_IMPROVEMENT", label: "Score Improvement" },
                    { value: "TOP_RANK", label: "Top Rank" },
                    { value: "PASS_RATE", label: "Pass Rate" },
                    { value: "SELECTION", label: "Selection" },
                  ].map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
                </select>
              </div>
              {["SCORE_ABOVE", "SCORE_IMPROVEMENT", "TOP_RANK"].includes(story.achievement) && (
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">Threshold</Label>
                  <Input
                    value={story.threshold}
                    onChange={e => setFormData(prev => ({
                      ...prev,
                      examSuccessStories: prev.examSuccessStories.map((x, j) => j === i ? { ...x, threshold: e.target.value } : x),
                    }))}
                    placeholder="e.g. 90%"
                    className="h-9"
                  />
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">Exam</Label>
                <select
                  value={story.subjectExamId}
                  onChange={e => setFormData(prev => ({
                    ...prev,
                    examSuccessStories: prev.examSuccessStories.map((x, j) => j === i ? { ...x, subjectExamId: e.target.value } : x),
                  }))}
                  className="w-full h-9 rounded-md border border-transparent bg-muted/60 px-3 text-sm transition-colors focus:bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                >
                  <option value="">Select exam…</option>
                  {exams.map(ex => <option key={ex.id} value={ex.id}>{ex.name}</option>)}
                </select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">Timeframe</Label>
                <select
                  value={story.timeframe}
                  onChange={e => setFormData(prev => ({
                    ...prev,
                    examSuccessStories: prev.examSuccessStories.map((x, j) => j === i ? { ...x, timeframe: e.target.value } : x),
                  }))}
                  className="w-full h-9 rounded-md border border-transparent bg-muted/60 px-3 text-sm transition-colors focus:bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                >
                  <option value="">Select…</option>
                  {[
                    { value: "LAST_1_YEAR", label: "Last 1 year" },
                    { value: "LAST_2_YEARS", label: "Last 2 years" },
                    { value: "LAST_3_YEARS", label: "Last 3 years" },
                    { value: "LAST_5_YEARS", label: "Last 5 years" },
                    { value: "ALL_TIME", label: "All time" },
                  ].map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
            </div>

            {/* Live preview sentence */}
            {previewSentence ? (
              <div className="flex items-start gap-2 rounded-lg bg-indigo-50 px-3 py-2.5">
                <Sparkles className="w-3.5 h-3.5 text-indigo-500 shrink-0 mt-0.5" />
                <p className="text-sm text-indigo-700 font-medium leading-snug">"{previewSentence}."</p>
              </div>
            ) : (
              <div className="flex items-start gap-2 rounded-lg bg-muted/50 border border-border/40 px-3 py-2.5">
                <Sparkles className="w-3.5 h-3.5 text-muted-foreground shrink-0 mt-0.5" />
                <p className="text-sm text-muted-foreground italic">Fill in the fields above to see your story preview.</p>
              </div>
            )}
          </div>
          );
        })}
      </div>

    </div>
  );

  const renderPhoto = () => {
    if (isDraftReadOnly) return viewPhoto();
    return (
    <div className="space-y-5">
      <div>
        <Label className="text-sm font-semibold text-foreground">Profile Photo <span className="text-destructive">*</span></Label>
        <p className="text-xs text-muted-foreground mt-0.5">A professional photo increases bookings by 40%. JPG, PNG up to 5MB.</p>
      </div>
      <input
        ref={photoInputRef}
        type="file"
        accept="image/jpeg,image/jpg,image/png,image/gif"
        onChange={handlePhotoChange}
        className="hidden"
      />
      {/* Mobile native camera capture */}
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="user"
        onChange={handlePhotoChange}
        className="hidden"
      />

      {/* Desktop webcam capture modal */}
      <Dialog open={isCameraOpen} onOpenChange={open => { if (!open) handleCloseCameraModal(); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Take a photo</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {cameraPhotoError ? (
              <div className="flex flex-col items-center justify-center gap-3 py-10 text-center">
                <Camera className="w-10 h-10 text-muted-foreground" />
                <p className="text-sm text-destructive">{cameraPhotoError}</p>
              </div>
            ) : (
              <div className="relative rounded-xl overflow-hidden bg-black aspect-video">
                <video ref={cameraPhotoPreviewRef} className="w-full h-full object-cover" muted playsInline />
              </div>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={handleCloseCameraModal}>Cancel</Button>
            {!cameraPhotoError && (
              <Button className="gap-2" onClick={handleTakeSnapshot}>
                <Camera className="w-4 h-4" /> Take Photo
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Crop modal */}
      <Dialog open={!!cropSrc} onOpenChange={open => { if (!open) setCropSrc(null); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Crop your photo</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {/* Aspect ratio selector */}
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-xs text-muted-foreground shrink-0">Crop shape:</span>
              {[
                { label: "1:1 · Headshot", value: 1, recommended: true },
                { label: "4:3", value: 4 / 3, recommended: false },
                { label: "3:2", value: 3 / 2, recommended: false },
                { label: "Free", value: undefined, recommended: false },
              ].map(opt => (
                <button
                  key={opt.label}
                  type="button"
                  onClick={() => setCropAspect(opt.value)}
                  className={cn(
                    "px-2.5 py-1 rounded-md text-xs font-medium border transition-colors",
                    cropAspect === opt.value
                      ? "border-primary bg-primary/10 text-primary"
                      : "border-border text-muted-foreground hover:border-primary/40"
                  )}
                >
                  {opt.label}{opt.recommended && <span className="ml-1 text-[10px] text-emerald-600 font-semibold">✓ Best</span>}
                </button>
              ))}
            </div>
            <p className="text-xs text-muted-foreground">Square crops show your face most clearly on student search cards.</p>

            <div className="relative rounded-xl overflow-hidden bg-black" style={{ height: 420 }}>
              {cropSrc && (
                <Cropper
                  image={cropSrc}
                  crop={crop}
                  zoom={zoom}
                  minZoom={0.5}
                  maxZoom={3}
                  rotation={rotation}
                  aspect={cropAspect}
                  onCropChange={setCrop}
                  onZoomChange={setZoom}
                  onCropComplete={onCropCompleteCallback}
                  restrictPosition={false}
                  showGrid
                />
              )}
            </div>
            {/* Zoom slider */}
            <div className="flex items-center gap-2">
              <ZoomOut className="w-4 h-4 text-muted-foreground shrink-0" />
              <input
                type="range" min={0.5} max={3} step={0.05} value={zoom}
                onChange={e => setZoom(+e.target.value)}
                className="flex-1 accent-primary h-1.5"
              />
              <ZoomIn className="w-4 h-4 text-muted-foreground shrink-0" />
            </div>
            {/* Rotation */}
            <div className="flex items-center justify-between">
              <button
                onClick={() => setRotation(r => (r - 90 + 360) % 360)}
                className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground px-2 py-1.5 rounded-lg hover:bg-accent transition-colors"
              >
                <RotateCcw className="w-3.5 h-3.5" /> Rotate left
              </button>
              <span className="text-xs text-muted-foreground">{rotation}°</span>
              <button
                onClick={() => setRotation(r => (r + 90) % 360)}
                className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground px-2 py-1.5 rounded-lg hover:bg-accent transition-colors"
              >
                Rotate right <RotateCw className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setCropSrc(null)}>Cancel</Button>
            <Button variant="outline" onClick={handlePhotoClick}>Choose different</Button>
            <Button className="gap-2" onClick={handleApplyCrop} disabled={cropApplying}>
              <Check className="w-4 h-4" />
              {cropApplying ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {(() => {
        const photoSrc = photoPreview || teacherProfile?.profileImage;
        return (
          <>
            {/* Photo guide card */}
            <div className="rounded-xl border border-border bg-muted/20 overflow-hidden">

              {/* Header */}
              <div className="px-4 pt-3 pb-2.5 flex items-start gap-2.5">
                <div className="w-8 h-8 rounded-lg bg-indigo-100 flex items-center justify-center shrink-0 mt-0.5">
                  <Camera className="w-4 h-4 text-indigo-600" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-foreground">How to take a great headshot</p>
                  <p className="text-[11px] text-muted-foreground mt-0.5">
                    Think passport photo — face close, plain background, look straight at the camera.
                  </p>
                </div>
              </div>

              {/* DO / AVOID columns */}
              <div className="grid grid-cols-2 border-t border-border/60">

                {/* DO column */}
                <div className="p-3 flex flex-col gap-2.5">
                  <p className="text-[11px] font-semibold text-emerald-600 flex items-center gap-1">
                    <span className="w-4 h-4 rounded-full bg-emerald-500 text-white text-[9px] flex items-center justify-center font-bold">✓</span>
                    Do this
                  </p>
                  <div className="flex items-start gap-3">
                    {/* Good illustration — larger */}
                    <div className="w-[88px] h-[88px] rounded-xl overflow-hidden border-2 border-emerald-400 shadow-sm shrink-0">
                      <svg viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
                        <rect width="120" height="120" fill="#eef4f0" />
                        {/* Subtle wall texture */}
                        <rect width="120" height="120" fill="#e8f0ea" />
                        <ellipse cx="60" cy="130" rx="50" ry="28" fill="#c8d8ca" />
                        <rect x="34" y="100" width="52" height="32" rx="7" fill="#c8d8ca" />
                        <rect x="52" y="84" width="16" height="20" rx="5" fill="#f0c89a" />
                        <ellipse cx="60" cy="56" rx="35" ry="40" fill="#f0c89a" />
                        <ellipse cx="60" cy="22" rx="35" ry="20" fill="#5c3d2e" />
                        <rect x="25" y="22" width="8" height="24" rx="4" fill="#5c3d2e" />
                        <rect x="87" y="22" width="8" height="24" rx="4" fill="#5c3d2e" />
                        <ellipse cx="25" cy="58" rx="5" ry="7" fill="#e8b88a" />
                        <ellipse cx="95" cy="58" rx="5" ry="7" fill="#e8b88a" />
                        <ellipse cx="45" cy="54" rx="6.5" ry="7" fill="white" />
                        <ellipse cx="75" cy="54" rx="6.5" ry="7" fill="white" />
                        <ellipse cx="46" cy="55" rx="3.8" ry="4.2" fill="#3d2b1f" />
                        <ellipse cx="76" cy="55" rx="3.8" ry="4.2" fill="#3d2b1f" />
                        <circle cx="47.5" cy="53.5" r="1.3" fill="white" />
                        <circle cx="77.5" cy="53.5" r="1.3" fill="white" />
                        <path d="M39 45 Q45 42 51 45" stroke="#5c3d2e" strokeWidth="1.8" strokeLinecap="round" fill="none" />
                        <path d="M69 45 Q75 42 81 45" stroke="#5c3d2e" strokeWidth="1.8" strokeLinecap="round" fill="none" />
                        <path d="M60 58 L57 68 Q60 71 63 68 Z" fill="#e8a87a" />
                        <path d="M47 78 Q60 90 73 78" stroke="#c0785a" strokeWidth="2.5" strokeLinecap="round" fill="none" />
                      </svg>
                    </div>
                    {/* Checklist */}
                    <ul className="space-y-1.5 pt-0.5">
                      <li className="text-[11px] text-foreground leading-snug flex items-start gap-1.5">
                        <span className="text-emerald-500 font-bold shrink-0 mt-px">✓</span>
                        Face fills most of the frame
                      </li>
                      <li className="text-[11px] text-foreground leading-snug flex items-start gap-1.5">
                        <span className="text-emerald-500 font-bold shrink-0 mt-px">✓</span>
                        Plain or blurred background
                      </li>
                      <li className="text-[11px] text-foreground leading-snug flex items-start gap-1.5">
                        <span className="text-emerald-500 font-bold shrink-0 mt-px">✓</span>
                        Bright, even lighting (near a window)
                      </li>
                      <li className="text-[11px] text-foreground leading-snug flex items-start gap-1.5">
                        <span className="text-emerald-500 font-bold shrink-0 mt-px">✓</span>
                        Look straight at camera, natural smile
                      </li>
                    </ul>
                  </div>
                </div>

                {/* AVOID column */}
                <div className="p-3 border-l border-border/60 flex flex-col gap-2.5">
                  <p className="text-[11px] font-semibold text-red-500 flex items-center gap-1">
                    <span className="w-4 h-4 rounded-full bg-red-500 text-white text-[9px] flex items-center justify-center font-bold">✕</span>
                    Avoid these
                  </p>
                  <div className="flex items-start gap-2">
                    {/* Bad examples stacked */}
                    <div className="flex flex-col gap-1.5 shrink-0">
                      {/* Too far */}
                      <div className="flex items-center gap-1.5">
                        <div className="w-[52px] h-[52px] rounded-lg overflow-hidden border border-red-300 shadow-sm relative">
                          <svg viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
                            <rect width="120" height="120" fill="#87CEEB" />
                            <rect x="0" y="82" width="120" height="38" fill="#7aab8a" />
                            <rect x="2" y="46" width="17" height="37" fill="#8eafc0" />
                            <rect x="101" y="54" width="17" height="28" fill="#8eafc0" />
                            <ellipse cx="60" cy="65" rx="6" ry="7" fill="#f5c9a0" />
                            <ellipse cx="60" cy="58" rx="6" ry="4" fill="#5c3d2e" />
                            <rect x="54" y="72" width="12" height="22" rx="3" fill="#4a6fa5" />
                            <rect x="47" y="73" width="7" height="3" rx="1.5" fill="#4a6fa5" />
                            <rect x="66" y="73" width="7" height="3" rx="1.5" fill="#4a6fa5" />
                            <rect x="55" y="94" width="5" height="11" rx="2" fill="#333" />
                            <rect x="62" y="94" width="5" height="11" rx="2" fill="#333" />
                          </svg>
                          <span className="absolute top-0.5 right-0.5 bg-red-500 text-white text-[8px] font-bold w-3.5 h-3.5 rounded-full flex items-center justify-center">✕</span>
                        </div>
                        <p className="text-[10px] text-muted-foreground leading-tight">Too far<br/>away</p>
                      </div>
                      {/* Sunglasses / dark */}
                      <div className="flex items-center gap-1.5">
                        <div className="w-[52px] h-[52px] rounded-lg overflow-hidden border border-red-300 shadow-sm relative">
                          <svg viewBox="0 0 120 120" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full">
                            <rect width="120" height="120" fill="#1e1e2e" />
                            <ellipse cx="60" cy="130" rx="44" ry="26" fill="#111" />
                            <rect x="38" y="100" width="44" height="30" rx="5" fill="#111" />
                            <rect x="52" y="84" width="16" height="18" rx="4" fill="#c49060" />
                            <ellipse cx="60" cy="56" rx="32" ry="36" fill="#c49060" />
                            <ellipse cx="60" cy="24" rx="32" ry="18" fill="#0a0500" />
                            <rect x="28" y="24" width="7" height="18" rx="3.5" fill="#0a0500" />
                            <rect x="85" y="24" width="7" height="18" rx="3.5" fill="#0a0500" />
                            <rect x="28" y="48" width="64" height="20" rx="5" fill="#0d0d0d" />
                            <rect x="30" y="50" width="26" height="16" rx="4" fill="#1a3a5c" opacity="0.9" />
                            <rect x="64" y="50" width="26" height="16" rx="4" fill="#1a3a5c" opacity="0.9" />
                            <rect x="56" y="55" width="8" height="3.5" rx="1.5" fill="#222" />
                            <path d="M60 69 L57 77 Q60 79 63 77 Z" fill="#a07040" />
                            <path d="M50 86 Q60 88 70 86" stroke="#906040" strokeWidth="2" strokeLinecap="round" fill="none" />
                          </svg>
                          <span className="absolute top-0.5 right-0.5 bg-red-500 text-white text-[8px] font-bold w-3.5 h-3.5 rounded-full flex items-center justify-center">✕</span>
                        </div>
                        <p className="text-[10px] text-muted-foreground leading-tight">Dark or<br/>sunglasses</p>
                      </div>
                    </div>
                    {/* Avoid list */}
                    <ul className="space-y-1.5 pt-0.5">
                      <li className="text-[11px] text-foreground leading-snug flex items-start gap-1.5">
                        <span className="text-red-400 font-bold shrink-0 mt-px">✕</span>
                        Standing far from camera
                      </li>
                      <li className="text-[11px] text-foreground leading-snug flex items-start gap-1.5">
                        <span className="text-red-400 font-bold shrink-0 mt-px">✕</span>
                        Sunglasses or hats
                      </li>
                      <li className="text-[11px] text-foreground leading-snug flex items-start gap-1.5">
                        <span className="text-red-400 font-bold shrink-0 mt-px">✕</span>
                        Dark or busy background
                      </li>
                      <li className="text-[11px] text-foreground leading-snug flex items-start gap-1.5">
                        <span className="text-red-400 font-bold shrink-0 mt-px">✕</span>
                        Group photos or selfies at an angle
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Pro tip */}
              <div className="px-4 py-2.5 border-t border-border/60 bg-amber-50/60 flex items-center gap-2">
                <span className="text-amber-500 text-sm shrink-0">💡</span>
                <p className="text-[11px] text-amber-800 leading-snug">
                  <strong>Quick tip:</strong> Sit near a window, hold your phone at eye level about arm's length away, and tap your face to focus before shooting.
                </p>
              </div>
            </div>

            {/* Upload zone / success preview */}
            {photoSrc ? (
              /* ── After upload: previews + change buttons ── */
              <div className="rounded-2xl border border-emerald-200 bg-emerald-50/40 p-4">
                <p className="text-xs font-semibold text-emerald-700 mb-3 flex items-center gap-1.5">
                  <span className="inline-block w-2 h-2 rounded-full bg-emerald-500" />
                  Photo uploaded — here's how you appear to students
                </p>
                <div className="flex items-start gap-5 flex-wrap">
                  {/* Profile page preview — square with gradient border (matches ProfileHero) */}
                  <div className="flex flex-col items-center gap-1.5 shrink-0">
                    <div className="p-[3px] rounded-2xl bg-gradient-to-br from-slate-400/60 to-slate-400/20 shadow-md">
                      <img src={photoSrc} alt="Profile" className="w-[72px] h-[72px] rounded-[11px] object-cover block" />
                    </div>
                    <p className="text-[10px] text-muted-foreground text-center leading-tight">Profile<br/>page</p>
                  </div>

                  <div className="self-center text-slate-300 text-base">→</div>

                  {/* Catalog card preview — 4:3 rectangle with gradient + name overlay (matches TeacherCard) */}
                  <div className="flex flex-col items-center gap-1.5 shrink-0">
                    <div className="w-[120px] rounded-xl overflow-hidden shadow-sm border border-slate-200">
                      <div className="relative w-full" style={{ aspectRatio: "4/3" }}>
                        <img src={photoSrc} alt="Search card" className="w-full h-full object-cover" />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/20 to-transparent" />
                        <div className="absolute bottom-0 left-0 right-0 px-2 pb-2">
                          <p className="text-white font-extrabold text-[10px] leading-tight drop-shadow truncate">
                            {currentUser?.name || "Your Name"}
                          </p>
                          <p className="text-white/70 text-[9px] mt-0.5">@handle</p>
                        </div>
                      </div>
                    </div>
                    <p className="text-[10px] text-muted-foreground text-center leading-tight">Search<br/>results</p>
                  </div>

                  <div className="ml-auto flex flex-col gap-2 self-center">
                    <Button type="button" variant="outline" size="sm" className="gap-1.5 text-xs" onClick={handlePhotoClick}>
                      <Upload className="w-3 h-3" /> Upload Photo
                    </Button>
                    <Button type="button" variant="outline" size="sm" className="gap-1.5 text-xs" onClick={handleOpenCamera}>
                      <Camera className="w-3 h-3" /> Retake From Camera
                    </Button>
                  </div>
                </div>
              </div>
            ) : (
              /* ── No photo yet: upload zone ── */
              <div className="border-2 border-dashed border-border rounded-2xl p-5 text-center flex flex-col items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center">
                  <Camera className="w-5 h-5 text-muted-foreground" />
                </div>
                <div>
                  <p className="font-medium text-foreground mb-0.5">Upload your headshot</p>
                  <p className="text-xs text-muted-foreground">JPG or PNG · up to 5 MB · square crop works best</p>
                </div>
                <div className="flex items-center gap-2">
                  <Button type="button" variant="outline" size="sm" className="gap-1.5" onClick={handlePhotoClick}>
                    <Upload className="w-3.5 h-3.5" /> Upload photo
                  </Button>
                  <Button type="button" variant="outline" size="sm" className="gap-1.5" onClick={handleOpenCamera}>
                    <Camera className="w-3.5 h-3.5" /> Take photo
                  </Button>
                </div>
              </div>
            )}
          </>
        );
      })()}
    </div>
  );
  };

  const renderVideo = () => {
    const token = auth.user?.access_token || sessionStorage.getItem('access_token');
    if (!token) return <p className="text-muted-foreground">Please log in to upload a video.</p>;

    // ── Script generation (structured fields only — no raw HTML from bio) ────────
    const resolvedSubjects = formData.subjectCombinations
      .map(c => subjects.find(s => s.id === c.subject)?.name)
      .filter(Boolean) as string[];
    const subjectText = resolvedSubjects.length > 0 ? resolvedSubjects.join(", ") : null;

    const modeMap: Record<string, string> = { ONLINE: "online", OFFLINE: "in-person", HYBRID: "both online and in-person" };
    const modeText = formData.teachingModes.length > 0
      ? formData.teachingModes.map(m => modeMap[m] ?? m).join(" and ")
      : null;

    const yearsExp = formData.yearsTeachingExperience ? String(formData.yearsTeachingExperience) : null;
    const expText = yearsExp ? `${yearsExp} year${yearsExp === "1" ? "" : "s"} of teaching experience` : null;

    const audienceLabels: Record<string, string> = {
      PRIMARY:       "Grade 1–3",
      UPPER_PRIMARY: "Grade 4–6",
      MIDDLE_SCHOOL: "Grade 7–9",
      GRADE_10:      "Grade 10",
      GRADE_11_12:   "Grade 11–12",
      UNIVERSITY:    "university students",
      ADULT:         "adult learners",
    };
    const audienceList = (formData.teachingAudience as string[]).map(a => audienceLabels[a] ?? a);
    const audienceText = audienceList.length > 0 ? audienceList.join(", ") : null;

    // teachingApproach in formData is already an array of plain-text tag strings
    const approachTags = (formData.teachingApproach as string[]).filter(Boolean);
    const approachText = approachTags.length > 0 ? approachTags.join(", ") : null;

    const qual = formData.highestQualification && formData.highestQualification !== "OTHER"
      ? formData.highestQualification : null;
    const qualInstitution = (formData as any).highestQualificationInstitution || null;

    const name = identityName || "there";

    // ── Paragraph 1: Introduction ─────────────────────────────────────────────
    const para1Parts: string[] = [];
    para1Parts.push([
      `Hi, I'm ${name}`,
      subjectText ? `and I teach ${subjectText}` : null,
      expText ? `with ${expText}` : null,
    ].filter(Boolean).join(", ") + ".");
    if (qual) {
      para1Parts.push(qualInstitution
        ? `I hold a ${qual} from ${qualInstitution}.`
        : `I hold a ${qual}.`);
    }
    const para1 = para1Parts.join(" ");

    // ── Paragraph 2: Teaching style & audience ────────────────────────────────
    const para2Parts: string[] = [];
    const whoHow: string[] = [];
    if (audienceText) whoHow.push(`I work with ${audienceText}`);
    if (modeText) whoHow.push(whoHow.length > 0 ? modeText : `I teach ${modeText}`);
    if (whoHow.length > 0) para2Parts.push(whoHow.join(", ") + ".");
    if (approachText) {
      para2Parts.push(`My teaching style is ${approachText} — I focus on building real understanding, not just exam scores.`);
    }
    const para2 = para2Parts.join(" ");

    // ── Paragraph 3: Call to action ───────────────────────────────────────────
    const para3 = `If you're looking for ${subjectText ? `${resolvedSubjects[0]} ` : ""}help, I'd love to work with you. Check out my profile and let's get started!`;

    // Join paragraphs with double newline so VideoRecorder renders them separately
    const generatedScript = [para1, para2, para3].filter(Boolean).join("\n\n");
    const displayScript = videoScript || generatedScript;

    // ── Render ───────────────────────────────────────────────────────────────────
    return (
      <div className="space-y-5">
        <div>
          <Label className="text-sm font-semibold text-foreground">Intro Video <span className="text-destructive">*</span></Label>
          <p className="text-xs text-muted-foreground mt-0.5">Teachers with a video get 3× more bookings. Keep it under 40 seconds.</p>
        </div>

        {/* ── Video tips guide card ─────────────────────────────────────────────── */}
        <div className="rounded-xl border border-border bg-muted/20 overflow-hidden">

          {/* Header */}
          <div className="px-4 pt-3 pb-2.5 flex items-start gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-violet-100 flex items-center justify-center shrink-0 mt-0.5">
              <Video className="w-4 h-4 text-violet-600" />
            </div>
            <div>
              <p className="text-sm font-semibold text-foreground">How to record a great intro video</p>
              <p className="text-[11px] text-muted-foreground mt-0.5">
                Speak naturally, face the light, keep it under 40 seconds — students decide in the first 5.
              </p>
            </div>
          </div>

          {/* GIF left + Do/Avoid right */}
          <div className="border-t border-border/60 flex">

            {/* GIF */}
            <div className="border-r border-border/60 bg-muted/10 flex items-center justify-center p-3" style={{ flex: '0 0 60%' }}>
              <img
                src="/bookmycoaching-tutor-video-tips.gif"
                alt="Good video example"
                className="block w-full h-auto rounded-lg"
              />
            </div>

            {/* Do / Avoid stacked */}
            <div className="flex-1 flex flex-col divide-y divide-border/60">

              {/* DO */}
              <div className="p-3 flex flex-col gap-2">
                <p className="text-xs font-semibold text-emerald-600 flex items-center gap-1">
                  <span className="w-4 h-4 rounded-full bg-emerald-500 text-white text-[9px] flex items-center justify-center font-bold">✓</span>
                  Do this
                </p>
                <ul className="space-y-2">
                  <li className="text-xs text-foreground leading-snug flex items-start gap-1.5">
                    <span className="text-emerald-500 font-bold shrink-0 mt-px">✓</span>
                    Face a window — natural light
                  </li>
                  <li className="text-xs text-foreground leading-snug flex items-start gap-1.5">
                    <span className="text-emerald-500 font-bold shrink-0 mt-px">✓</span>
                    Plain or tidy background
                  </li>
                  <li className="text-xs text-foreground leading-snug flex items-start gap-1.5">
                    <span className="text-emerald-500 font-bold shrink-0 mt-px">✓</span>
                    Phone at eye level, held steady
                  </li>
                  <li className="text-xs text-foreground leading-snug flex items-start gap-1.5">
                    <span className="text-emerald-500 font-bold shrink-0 mt-px">✓</span>
                    Quiet room, speak clearly
                  </li>
                </ul>
              </div>

              {/* AVOID */}
              <div className="p-3 flex flex-col gap-2">
                <p className="text-xs font-semibold text-red-500 flex items-center gap-1">
                  <span className="w-4 h-4 rounded-full bg-red-500 text-white text-[9px] flex items-center justify-center font-bold">✕</span>
                  Avoid these
                </p>
                <ul className="space-y-2">
                  <li className="text-xs text-foreground leading-snug flex items-start gap-1.5">
                    <span className="text-red-400 font-bold shrink-0 mt-px">✕</span>
                    Backlight or dark room
                  </li>
                  <li className="text-xs text-foreground leading-snug flex items-start gap-1.5">
                    <span className="text-red-400 font-bold shrink-0 mt-px">✕</span>
                    Shaky or landscape-mode phone
                  </li>
                  <li className="text-xs text-foreground leading-snug flex items-start gap-1.5">
                    <span className="text-red-400 font-bold shrink-0 mt-px">✕</span>
                    Background noise or echo
                  </li>
                  <li className="text-xs text-foreground leading-snug flex items-start gap-1.5">
                    <span className="text-red-400 font-bold shrink-0 mt-px">✕</span>
                    Reading word-for-word
                  </li>
                  <li className="text-xs text-foreground leading-snug flex items-start gap-1.5">
                    <span className="text-red-400 font-bold shrink-0 mt-px">✕</span>
                    Longer than 40 seconds
                  </li>
                </ul>
              </div>

            </div>
          </div>

          {/* Pro tip */}
          <div className="px-4 py-2.5 border-t border-border/60 bg-amber-50/60 flex items-center gap-2">
            <span className="text-amber-500 text-sm shrink-0">💡</span>
            <p className="text-[11px] text-amber-800 leading-snug">
              <strong>Quick tip:</strong> Do one practice run, then record for real. Use the script below as a guide — glance at it, don't read it.
            </p>
          </div>
        </div>

        {/* ── Help card: script + structure + tips ─────────────────────────────── */}
        <div className="rounded-xl border border-border bg-muted/20 overflow-hidden">

          {/* Script section */}
          <div className="px-4 pt-4 pb-3">
            <div className="flex items-start justify-between gap-3 mb-2.5">
              <div>
                <p className="text-sm font-semibold text-foreground">🎙️ Your intro script</p>
                <p className="text-[11px] text-muted-foreground mt-0.5">Generated from your profile — edit it to match your voice. It will scroll on screen while you record.</p>
              </div>
              <button
                type="button"
                className="text-[11px] text-primary hover:underline shrink-0 mt-0.5"
                onClick={() => setVideoScript(generatedScript)}
              >
                Reset
              </button>
            </div>
            <div className="rounded-lg border-l-4 border-indigo-300 bg-white shadow-sm">
              <textarea
                value={displayScript}
                onChange={e => setVideoScript(e.target.value)}
                rows={5}
                className="w-full px-3 py-2.5 text-sm text-foreground leading-relaxed resize-none focus:outline-none bg-transparent placeholder:text-muted-foreground"
                placeholder="Your script will appear here…"
              />
              <div className="flex items-center justify-between px-3 pb-2">
                <p className="text-[10px] text-muted-foreground">Speak naturally — don't memorise it word for word.</p>
                <p className="text-[10px] text-muted-foreground">{displayScript.split(/\s+/).filter(Boolean).length} words</p>
              </div>
            </div>
          </div>

          {/* Structure guide — step timeline */}
          <div className="border-t border-border/60 px-4 py-4">
            <p className="text-[11px] font-semibold text-foreground mb-4">How to structure your 40 seconds</p>
            <div className="relative flex items-start justify-between gap-2">
              {/* Connecting line */}
              <div className="absolute top-4 left-[calc(16.67%-4px)] right-[calc(16.67%-4px)] h-px bg-border z-0" />

              {[
                {
                  step: 1,
                  time: "0 – 10s",
                  label: "Introduce yourself",
                  detail: "Your name, subject & years of experience",
                  numBg: "bg-violet-100",
                  numText: "text-violet-700",
                  timeBg: "bg-violet-50",
                  timeText: "text-violet-600",
                },
                {
                  step: 2,
                  time: "10 – 25s",
                  label: "Your teaching style",
                  detail: "How you teach & what makes you different",
                  numBg: "bg-indigo-100",
                  numText: "text-indigo-700",
                  timeBg: "bg-indigo-50",
                  timeText: "text-indigo-600",
                },
                {
                  step: 3,
                  time: "25 – 40s",
                  label: "Call to action",
                  detail: "Who you help & invite them to book",
                  numBg: "bg-emerald-100",
                  numText: "text-emerald-700",
                  timeBg: "bg-emerald-50",
                  timeText: "text-emerald-600",
                },
              ].map(s => (
                <div key={s.step} className="relative z-10 flex flex-col items-center text-center flex-1 gap-2">
                  {/* Step circle */}
                  <div className={`w-8 h-8 rounded-full ${s.numBg} ${s.numText} flex items-center justify-center text-sm font-bold ring-2 ring-white shadow-sm`}>
                    {s.step}
                  </div>
                  {/* Time badge */}
                  <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${s.timeBg} ${s.timeText}`}>
                    {s.time}
                  </span>
                  {/* Label + detail */}
                  <div>
                    <p className="text-[11px] font-semibold text-foreground leading-snug">{s.label}</p>
                    <p className="text-[10px] text-muted-foreground leading-snug mt-0.5">{s.detail}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Tips row */}
          <div className="border-t border-border/60 px-4 py-2.5 bg-amber-50/50 flex flex-wrap gap-x-5 gap-y-1.5">
            {[
              { icon: "🪟", text: "Face a window for natural light" },
              { icon: "🧱", text: "Plain wall or tidy background" },
              { icon: "📱", text: "Phone at eye level, arm's length" },
              { icon: "🔇", text: "Quiet room — audio matters" },
            ].map(tip => (
              <span key={tip.icon} className="text-[11px] text-amber-800 flex items-center gap-1.5">
                <span>{tip.icon}</span>{tip.text}
              </span>
            ))}
          </div>
        </div>

        {/* ── Upload / record / link card ───────────────────────────────────────── */}
        <IntroVideoUpload
          accessToken={token}
          readOnly={!canEdit() || draftStatus === 'PENDING_REVIEW'}
          onRemove={() => refreshProfile(true).catch(() => {})}
          teleprompterScript={displayScript}
        />
      </div>
    );
  };

  const isAboutValid =
    formData.teachingModes.length > 0 &&
    formData.subjectCombinations.filter(c => c.subject).length > 0;

  const isDescriptionValid =
    countHtmlWords(formData.bio) >= 50 &&
    formData.teachingApproach.length > 0;

  const isCredentialsValid =
    !!(formData.highestQualification || (teacherProfile as any)?.highestQualification);

  // Map section ID to review status from sections config
  const getSectionStatus = (sectionId: string): string => {
    const sec = sections.find((s) => s.id === sectionId);
    return sec?.reviewStatus || "DRAFT";
  };

  // View renderers (read-only)
  const viewAbout = () => {
    const modeStyles: Record<string, string> = {
      ONLINE:  "bg-amber-100 text-amber-700",
      OFFLINE: "bg-[hsl(210,60%,18%)]/10 text-[hsl(210,60%,28%)]",
      HYBRID:  "bg-amber-100 text-amber-700",
    };
    const modeLabels: Record<string, string> = { ONLINE: "Online", OFFLINE: "Offline", HYBRID: "Hybrid" };
    const subjectColors = [
      "bg-orange-100 text-orange-700",
      "bg-pink-100 text-pink-700",
      "bg-amber-100 text-amber-700",
      "bg-green-100 text-green-700",
      "bg-yellow-100 text-yellow-700",
      "bg-indigo-100 text-indigo-700",
    ];
    const profileImg = photoPreview || teacherProfile?.profileImage;
    const subjects = teacherProfile?.subjects || [];
    const modes = teacherProfile?.teachingModes || [];

    return (
      <div className="space-y-5">
        {/* Identity row — avatar + core info */}
        <div className="flex items-start gap-4">
          {/* Avatar */}
          {profileImg ? (
            <img
              src={profileImg}
              alt={identityName || "Profile"}
              className="w-16 h-16 rounded-full object-cover ring-2 ring-slate-100 shrink-0"
            />
          ) : (
            <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center shrink-0 ring-2 ring-slate-200">
              <User className="w-7 h-7 text-slate-400" />
            </div>
          )}
          <div className="flex-1 min-w-0 space-y-1.5 pt-0.5">
            <p className="text-lg font-bold text-slate-900 leading-tight">{identityName || <span className="text-slate-400 italic font-normal">Name not set</span>}</p>
            {handle ? (
              <a
                href={`${window.location.origin}/t/${handle}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-sm font-medium text-[hsl(210,60%,38%)] hover:underline"
              >
                @{handle}
                <ExternalLink className="w-3 h-3 opacity-60 shrink-0" />
              </a>
            ) : (
              <p className="text-sm text-slate-400 italic">Handle not set</p>
            )}
            {/* Phone + Location inline */}
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-slate-500">
              {identityPhone && (
                <span className="flex items-center gap-1">
                  <Phone className="w-3.5 h-3.5 shrink-0" />
                  {identityPhone}
                </span>
              )}
              {(teacherProfile?.locationDisplayName || teacherProfile?.city) && (
                <span className="flex items-center gap-1">
                  <MapPin className="w-3.5 h-3.5 shrink-0" />
                  {formatTeacherLocation(teacherProfile.locationDisplayName, teacherProfile.city, teacherProfile.state, teacherProfile.country)}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className="border-t border-slate-100" />

        {/* Teaching Mode */}
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-slate-400 mb-2">Teaching Mode</p>
          {modes.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {modes.map((m: string) => (
                <span key={m} className={`text-xs px-3 py-1 rounded-full font-semibold ${modeStyles[m] || "bg-slate-100 text-slate-600"}`}>
                  {modeLabels[m] || m}
                </span>
              ))}
            </div>
          ) : (
            <p className="text-sm text-slate-400 italic">Not set</p>
          )}
        </div>

        {/* Subjects */}
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-slate-400 mb-2">Subjects</p>
          {subjects.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {subjects.map((s: any, idx: number) => (
                <span key={s.subjectId || s.subject?.id} className={`text-xs px-3 py-1 rounded-full font-semibold ${subjectColors[idx % subjectColors.length]}`}>
                  {s.subjectName || s.subject?.name}
                </span>
              ))}
            </div>
          ) : (
            <p className="text-sm text-slate-400 italic">Not set</p>
          )}
        </div>
      </div>
    );
  };

  // Decode HTML entities
  const decodeHtml = (html: string) => {
    const txt = document.createElement('textarea');
    txt.innerHTML = html;
    return txt.value;
  };

  const viewDescription = () => (
    <div className="space-y-4 text-sm">
      {!teacherProfile?.professionalBio && !teacherProfile?.teachingApproach && (
        <p className="text-muted-foreground italic">Not filled in yet</p>
      )}


      {/* Professional Bio card */}
      {teacherProfile?.professionalBio && (
        <div className="rounded-xl bg-slate-50 overflow-hidden">
          <div className="flex items-center gap-2 px-4 py-4 border-b border-slate-200 bg-white">
            <FileText className="w-4 h-4 text-[hsl(210,60%,28%)] shrink-0" />
            <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Professional Bio</p>
          </div>
          <div className="px-4 py-4 text-foreground leading-relaxed prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: teacherProfile.professionalBio }} />
        </div>
      )}

      {/* Teaching Approach card */}
      {teacherProfile?.teachingApproach && (
        <div className="rounded-xl bg-slate-50 overflow-hidden">
          <div className="flex items-center gap-2 px-4 py-4 border-b border-slate-200 bg-white">
            <GraduationCap className="w-4 h-4 text-[hsl(210,60%,28%)] shrink-0" />
            <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Teaching Approach</p>
          </div>
          {(() => {
            // Legacy profiles stored free text here — render it as text, not as chips.
            const tags = teacherProfile.teachingApproach.split(",").map((s: string) => s.trim())
              .filter((s: string) => TEACHING_APPROACH_OPTIONS.includes(s));
            return tags.length > 0 ? (
              <div className="px-4 py-4 flex flex-wrap gap-2">
                {tags.map((tag: string) => (
                  <span key={tag} className="px-3 py-1 rounded-full text-sm font-medium bg-primary/10 text-primary border border-primary/20">{tag}</span>
                ))}
              </div>
            ) : (
              <p className="px-4 py-4 text-sm text-slate-700 leading-relaxed">{teacherProfile.teachingApproach}</p>
            );
          })()}
        </div>
      )}
    </div>
  );

  const handleSaveCredentials = async () => {
    if (formData.highestQualification && !formData.highestQualificationInstitution.trim()) {
      toast.error('Please add the institution where you earned your highest qualification.');
      return;
    }
    if (formData.highestQualification === "OTHER" && !formData.otherHighestQualification.trim()) {
      toast.error('Please specify your other highest qualification.');
      return;
    }
    if (formData.teachingQualification === "OTHER" && !formData.otherTeachingQualification.trim()) {
      toast.error('Please specify your other teaching qualification.');
      return;
    }
    const token = await getToken();
    if (!token) { toast.error('Authentication required'); return; }
    try {
      await saveEducation({
        highestQualification: formData.highestQualification || undefined,
        otherHighestQualification: formData.highestQualification === "OTHER" ? formData.otherHighestQualification || undefined : undefined,
        highestQualificationInstitution: formData.highestQualificationInstitution || undefined,
        teachingQualification: formData.teachingQualification || undefined,
        otherTeachingQualification: formData.teachingQualification === "OTHER" ? formData.otherTeachingQualification || undefined : undefined,
        yearsTeachingExperience: formData.yearsTeachingExperience || undefined,
        formerSchoolTeacher: formData.formerSchoolTeacher,
        formerInstitutionName: formData.formerSchoolTeacher ? formData.formerInstitutionName || undefined : undefined,
        formerInstitutionYears: formData.formerSchoolTeacher && formData.formerInstitutionYears !== "" ? Number(formData.formerInstitutionYears) : undefined,
      }, token);
      toast.success('Credentials saved');
      setEditingSection(null);
      await refreshProfile(true);
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.response?.data?.error || err?.message || 'Failed to save credentials';
      toast.error(msg);
    }
  };

  const viewQualification = () => (
    <div className="space-y-8 text-sm">

      {/* ── Academic Credentials + Teaching Background ── */}
      {(isDraftReadOnly || (hasActiveVersion && !hasDraft)) ? (
        <div className="space-y-6">

          {/* Academic Credentials — read-only */}
          <div className="space-y-3">
            <p className="text-xs font-semibold uppercase tracking-widest text-slate-400">Academic Credentials</p>
            <div className="rounded-xl bg-slate-50 divide-y divide-slate-100 overflow-hidden">
              {formData.highestQualification && (
                <div className="flex items-start gap-3 px-4 py-3">
                  <GraduationCap className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
                  <div>
                    <p className="text-xs text-slate-400">Highest Qualification</p>
                    <p className="text-sm font-medium text-slate-800 mt-0.5">
                      {({
                        BELOW_SECONDARY: "Below Secondary",
                        SECONDARY: "Secondary (10th / O-Level)",
                        DIPLOMA: "Diploma / Certificate",
                        BACHELOR: "Bachelor's Degree",
                        MASTER: "Master's Degree",
                        DOCTORATE: "Doctorate (PhD)",
                        OTHER: "Other",
                      } as Record<string, string>)[formData.highestQualification] ?? formData.highestQualification}
                      {formData.highestQualification === "OTHER" && formData.otherHighestQualification
                        ? ` — ${formData.otherHighestQualification}`
                        : ""}
                    </p>
                  </div>
                </div>
              )}
              {formData.highestQualificationInstitution && (
                <div className="flex items-start gap-3 px-4 py-3">
                  <Briefcase className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
                  <div>
                    <p className="text-xs text-slate-400">Institution</p>
                    <p className="text-sm font-medium text-slate-800 mt-0.5">{formData.highestQualificationInstitution}</p>
                  </div>
                </div>
              )}
              {formData.teachingQualification && formData.teachingQualification !== "NONE" && (
                <div className="flex items-start gap-3 px-4 py-3">
                  <FileText className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
                  <div>
                    <p className="text-xs text-slate-400">Teaching Qualification</p>
                    <p className="text-sm font-medium text-slate-800 mt-0.5">
                      {({
                        PGCE: "PGCE",
                        BED: "B.Ed",
                        DELED: "D.El.Ed",
                        CTET: "CTET / State TET",
                        OTHER: "Other",
                      } as Record<string, string>)[formData.teachingQualification] ?? formData.teachingQualification}
                      {formData.teachingQualification === "OTHER" && formData.otherTeachingQualification
                        ? ` — ${formData.otherTeachingQualification}`
                        : ""}
                    </p>
                  </div>
                </div>
              )}
              {formData.yearsTeachingExperience && (
                <div className="flex items-start gap-3 px-4 py-3">
                  <TrendingUp className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
                  <div>
                    <p className="text-xs text-slate-400">Teaching Experience</p>
                    <p className="text-sm font-medium text-slate-800 mt-0.5">
                      {({
                        LESS_THAN_1: "Less than 1 year",
                        ONE_TO_3: "1–3 years",
                        THREE_TO_5: "3–5 years",
                        FIVE_TO_10: "5–10 years",
                        MORE_THAN_10: "More than 10 years",
                      } as Record<string, string>)[formData.yearsTeachingExperience] ?? formData.yearsTeachingExperience}
                    </p>
                  </div>
                </div>
              )}
              {!formData.highestQualification && !formData.teachingQualification && !formData.yearsTeachingExperience && (
                <div className="px-4 py-3 text-sm text-slate-400 italic">Not filled in yet</div>
              )}
            </div>
          </div>

          {/* Teaching Background — read-only */}
          <div className="space-y-3">
            <p className="text-xs font-semibold uppercase tracking-widest text-slate-400">Teaching Background</p>
            <div className="rounded-xl bg-slate-50 overflow-hidden">
              <div className="flex items-center gap-3 px-4 py-3">
                <Briefcase className="w-4 h-4 text-slate-400 shrink-0" />
                <div className="flex-1 flex items-center justify-between">
                  <p className="text-sm text-slate-600">Former school / institution teacher</p>
                  <span className={`text-xs font-semibold px-2.5 py-0.5 rounded-full ${formData.formerSchoolTeacher ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                    {formData.formerSchoolTeacher ? "Yes" : "No"}
                  </span>
                </div>
              </div>
              {formData.formerSchoolTeacher && (formData.formerInstitutionName || formData.formerInstitutionYears !== "") && (
                <div className="border-t border-slate-100 px-4 py-3 grid grid-cols-2 gap-4">
                  {formData.formerInstitutionName && (
                    <div>
                      <p className="text-xs text-slate-400">Institution</p>
                      <p className="text-sm font-medium text-slate-800 mt-0.5">{formData.formerInstitutionName}</p>
                    </div>
                  )}
                  {formData.formerInstitutionYears !== "" && formData.formerInstitutionYears !== undefined && (
                    <div>
                      <p className="text-xs text-slate-400">Years There</p>
                      <p className="text-sm font-medium text-slate-800 mt-0.5">{formData.formerInstitutionYears} {Number(formData.formerInstitutionYears) === 1 ? "year" : "years"}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

        </div>
      ) : (
        <div className="space-y-6">

          {/* Academic Credentials */}
          <div className="space-y-3">
            <div>
              <p className="text-sm font-semibold text-foreground">Academic Credentials</p>
              <p className="text-xs text-muted-foreground mt-0.5">Your highest qualification and teaching certification</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">Highest Qualification <FieldHint content={teacherProfileHelp} stepId="EDUCATION" fieldKey="highestQualification" /></Label>
                <select
                  value={formData.highestQualification}
                  onChange={e => setFormData(d => ({
                    ...d,
                    highestQualification: e.target.value,
                    // Clear the paired free-text whenever the selection is not 'OTHER'
                    otherHighestQualification: e.target.value === "OTHER" ? d.otherHighestQualification : "",
                  }))}
                  className="w-full h-9 rounded-md border border-transparent bg-muted/60 px-3 text-sm transition-colors focus:bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                >
                  <option value="">Select qualification…</option>
                  <option value="BELOW_SECONDARY">Below Secondary</option>
                  <option value="SECONDARY">Secondary (10th / O-Level)</option>
                  <option value="DIPLOMA">Diploma / Certificate</option>
                  <option value="BACHELOR">Bachelor's Degree</option>
                  <option value="MASTER">Master's Degree</option>
                  <option value="DOCTORATE">Doctorate (PhD)</option>
                  <option value="OTHER">Other</option>
                </select>
                {formData.highestQualification === "OTHER" && (
                  <>
                    <Label htmlFor="otherHighestQualification" className="sr-only">
                      Specify other highest qualification
                    </Label>
                    <Input
                      id="otherHighestQualification"
                      value={formData.otherHighestQualification}
                      onChange={e => setFormData(d => ({ ...d, otherHighestQualification: e.target.value }))}
                      placeholder="Please specify"
                      maxLength={100}
                      required
                      aria-required="true"
                      className="h-9 mt-2"
                    />
                  </>
                )}
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">
                  Institution {formData.highestQualification && <span className="text-destructive">*</span>}
                </Label>
                <Input
                  value={formData.highestQualificationInstitution}
                  onChange={e => setFormData(d => ({ ...d, highestQualificationInstitution: e.target.value }))}
                  placeholder="e.g. University of Delhi"
                  className={cn('h-9', formData.highestQualification && !formData.highestQualificationInstitution.trim() && 'border-red-500')}
                />
                {formData.highestQualification && !formData.highestQualificationInstitution.trim() && (
                  <p className="text-xs text-red-500">Required — where did you earn this qualification?</p>
                )}
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">Teaching Qualification <FieldHint content={teacherProfileHelp} stepId="EDUCATION" fieldKey="teachingQualification" /></Label>
                <select
                  value={formData.teachingQualification}
                  onChange={e => setFormData(d => ({
                    ...d,
                    teachingQualification: e.target.value,
                    // Clear the paired free-text whenever the selection is not 'OTHER'
                    otherTeachingQualification: e.target.value === "OTHER" ? d.otherTeachingQualification : "",
                  }))}
                  className="w-full h-9 rounded-md border border-transparent bg-muted/60 px-3 text-sm transition-colors focus:bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                >
                  <option value="">Select qualification…</option>
                  <option value="NONE">None</option>
                  <option value="PGCE">PGCE</option>
                  <option value="BED">B.Ed</option>
                  <option value="DELED">D.El.Ed</option>
                  <option value="CTET">CTET / State TET</option>
                  <option value="OTHER">Other</option>
                </select>
                {formData.teachingQualification === "OTHER" && (
                  <>
                    <Label htmlFor="otherTeachingQualification" className="sr-only">
                      Specify other teaching qualification
                    </Label>
                    <Input
                      id="otherTeachingQualification"
                      value={formData.otherTeachingQualification}
                      onChange={e => setFormData(d => ({ ...d, otherTeachingQualification: e.target.value }))}
                      placeholder="Please specify"
                      maxLength={100}
                      required
                      aria-required="true"
                      className="h-9 mt-2"
                    />
                  </>
                )}
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs text-muted-foreground">Years of Teaching Experience <FieldHint content={teacherProfileHelp} stepId="EDUCATION" fieldKey="experienceYears" /></Label>
                <select
                  value={formData.yearsTeachingExperience}
                  onChange={e => setFormData(d => ({ ...d, yearsTeachingExperience: e.target.value }))}
                  className="w-full h-9 rounded-md border border-transparent bg-muted/60 px-3 text-sm transition-colors focus:bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                >
                  <option value="">Select range…</option>
                  <option value="LESS_THAN_1">Less than 1 year</option>
                  <option value="ONE_TO_3">1–3 years</option>
                  <option value="THREE_TO_5">3–5 years</option>
                  <option value="FIVE_TO_10">5–10 years</option>
                  <option value="MORE_THAN_10">More than 10 years</option>
                </select>
              </div>
            </div>
          </div>

          {/* Teaching Background */}
          <div className="space-y-3 pt-4 border-t border-border/60">
            <div>
              <p className="text-sm font-semibold text-foreground">Teaching Background</p>
              <p className="text-xs text-muted-foreground mt-0.5">Prior experience in schools or institutions</p>
            </div>
            <div className="flex items-center justify-between py-1">
              <Label className="text-sm text-foreground cursor-pointer">Former school / institution teacher? <FieldHint content={teacherProfileHelp} stepId="EDUCATION" fieldKey="formerSchoolTeacher" /></Label>
              <button
                type="button"
                onClick={() => setFormData(d => ({ ...d, formerSchoolTeacher: !d.formerSchoolTeacher }))}
                className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors focus:outline-none ${formData.formerSchoolTeacher ? 'bg-primary' : 'bg-muted-foreground/30'}`}
              >
                <span className={`inline-block h-3.5 w-3.5 rounded-full bg-white shadow transition-transform ${formData.formerSchoolTeacher ? 'translate-x-4' : 'translate-x-1'}`} />
              </button>
            </div>
            {formData.formerSchoolTeacher && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">Institution Name</Label>
                  <Input
                    value={formData.formerInstitutionName}
                    onChange={e => setFormData(d => ({ ...d, formerInstitutionName: e.target.value }))}
                    placeholder="e.g. Delhi Public School"
                    className="h-9"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-muted-foreground">Years Taught There</Label>
                  <Input
                    type="number"
                    min={1}
                    max={30}
                    value={formData.formerInstitutionYears === "" ? "" : String(formData.formerInstitutionYears)}
                    onChange={e => {
                      if (e.target.value === "") {
                        setFormData(d => ({ ...d, formerInstitutionYears: "" }));
                      } else {
                        const clamped = Math.min(30, Math.max(1, Number(e.target.value)));
                        setFormData(d => ({ ...d, formerInstitutionYears: clamped }));
                      }
                    }}
                    placeholder="1–30"
                    className="h-9"
                  />
                </div>
              </div>
            )}
          </div>

        </div>
      )}
    </div>
  );

  const viewPhoto = () => {
    const img = photoPreview || teacherProfile?.profileImage;
    return (
      <div className="flex flex-col items-center gap-4 py-2">
        {img ? (
          <div className="relative group">
            <div className="w-36 h-36 rounded-full overflow-hidden ring-4 ring-slate-100 shadow-md">
              <img src={img} alt="Profile photo" className="w-full h-full object-cover" />
            </div>
            <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 text-xs font-semibold bg-emerald-500 text-white px-3 py-0.5 rounded-full shadow-sm whitespace-nowrap">
              Photo uploaded
            </span>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-3">
            <div className="w-36 h-36 rounded-full bg-slate-100 flex items-center justify-center ring-4 ring-slate-200">
              <Camera className="w-12 h-12 text-slate-300" />
            </div>
            <p className="text-sm text-slate-500">No profile photo yet</p>
          </div>
        )}
        {img && (
          <p className="text-xs text-slate-400 text-center max-w-xs">
            This photo is shown to students on your public profile and listing cards.
          </p>
        )}
      </div>
    );
  };

  const viewVideo = () => {
    const token = auth.user?.access_token || sessionStorage.getItem('access_token');
    if (!token) return <p className="text-sm text-muted-foreground italic">No intro video uploaded yet</p>;
    // In view mode, show video player without edit controls
    return (
      <div className="max-w-2xl">
        <IntroVideoUpload
          accessToken={token}
          readOnly={!canEdit() || draftStatus === 'PENDING_REVIEW'}
          onRemove={() => refreshProfile(true).catch(() => {})}
        />
      </div>
    );
  };

  // Overall approved count
  const approvedCount = sections.filter((s) => s.reviewStatus === "APPROVED").length;
  const totalSections = sections.length;

  // Determine if editing should be blocked (read-only mode)

  const renderAdminComment = (sectionType: string) => {
    const comment = getAdminComment(sectionType);
    if (!comment || !isDraftRejected) return null;
    return (
      <div className="mt-2 rounded-lg bg-destructive/5 p-3 flex items-start gap-2">
        <AlertCircle className="w-4 h-4 text-destructive shrink-0 mt-0.5" />
        <div>
          <p className="text-xs font-medium text-destructive">Admin Comment</p>
          <p className="text-xs text-muted-foreground mt-0.5">{comment}</p>
        </div>
      </div>
    );
  };

  const [showSubmitDialog, setShowSubmitDialog] = useState(false);
  const [showNextStepDialog, setShowNextStepDialog] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [currentStep, setCurrentStep] = useState(0);

  const wizardSteps = [
    { id: 'about',         label: 'About',          description: 'Name, location, subjects & teaching mode', icon: User,          sectionType: 'BASIC_INFO',  optional: false },
    { id: 'description',   label: 'Bio',            description: 'Professional bio & teaching philosophy',   icon: FileText,      sectionType: 'DESCRIPTION', optional: false },
    { id: 'qualification', label: 'Credentials',    description: 'Degrees and education',                    icon: GraduationCap, sectionType: 'EDUCATION',   optional: false },
    { id: 'photo',         label: 'Photo',          description: 'Professional headshot students will see',  icon: Camera,        sectionType: 'MEDIA',       optional: false },
    { id: 'video',         label: 'Intro Video',    description: '30-second intro clip — 3× more bookings',  icon: Video,         sectionType: 'MEDIA',       optional: false },
  ];

  // A step is complete only when it actually has saved data — not just because the user navigated past it.
  const stepCompleted = [
    isAboutValid,
    isDescriptionValid,
    !!((teacherProfile as any)?.highestQualification),
    !!(photoPreview || teacherProfile?.profileImage),
    !!(teacherProfile?.introVideo),
  ];

  // All 5 steps are required to submit.
  // For video: also accept liveVideoStatus from the upload hook's query cache (introVideo on teacherProfile may be stale)
  const hasVideo = stepCompleted[4] || liveVideoStatus?.status === 'READY';
  const canSubmitForReview = stepCompleted[0] && stepCompleted[1] && stepCompleted[2] && stepCompleted[3] && hasVideo;
  // Steps still blocking submission — shown to the user so the disabled button explains itself.
  const incompleteSteps = wizardSteps
    .map((s, i) => ({ ...s, index: i, done: i === 4 ? hasVideo : stepCompleted[i] }))
    .filter(s => !s.done);

  const handleWizardNext = async () => {
    if (currentStep === 0) {
      const ok = await handleSaveAbout();
      if (!ok) return;
    } else if (currentStep === 1) {
      const ok = await handleSaveDescription();
      if (!ok) return;
    } else if (currentStep === 2) {
      if (!formData.highestQualification) {
        toast.error('Please select your highest qualification before continuing');
        return;
      }
      if (!formData.highestQualificationInstitution.trim()) {
        toast.error('Please add the institution where you earned your highest qualification.');
        return;
      }
      if (formData.highestQualification === "OTHER" && !formData.otherHighestQualification.trim()) {
        toast.error('Please specify your other highest qualification.');
        return;
      }
      if (formData.teachingQualification === "OTHER" && !formData.otherTeachingQualification.trim()) {
        toast.error('Please specify your other teaching qualification.');
        return;
      }
      const tp = teacherProfile as any;
      const unchanged =
        (formData.highestQualification || "") === (tp?.highestQualification ?? "") &&
        (formData.otherHighestQualification || "") === (tp?.otherHighestQualification ?? "") &&
        (formData.highestQualificationInstitution || "") === (tp?.highestQualificationInstitution ?? "") &&
        (formData.teachingQualification || "") === (tp?.teachingQualification ?? "") &&
        (formData.otherTeachingQualification || "") === (tp?.otherTeachingQualification ?? "") &&
        (formData.yearsTeachingExperience || "") === (tp?.yearsTeachingExperience ?? "") &&
        formData.formerSchoolTeacher === (tp?.formerSchoolTeacher ?? false) &&
        (formData.formerInstitutionName || "") === (tp?.formerInstitutionName ?? "") &&
        (formData.formerInstitutionYears === "" ? null : Number(formData.formerInstitutionYears)) === (tp?.formerInstitutionYears ?? null);

      if (!unchanged) {
        const token = await getToken();
        if (!token) { toast.error('Authentication required'); return; }
        try {
          await saveEducation({
            highestQualification: formData.highestQualification || undefined,
            otherHighestQualification: formData.highestQualification === "OTHER" ? formData.otherHighestQualification || undefined : undefined,
            highestQualificationInstitution: formData.highestQualificationInstitution || undefined,
            teachingQualification: formData.teachingQualification || undefined,
            otherTeachingQualification: formData.teachingQualification === "OTHER" ? formData.otherTeachingQualification || undefined : undefined,
            yearsTeachingExperience: formData.yearsTeachingExperience || undefined,
            formerSchoolTeacher: formData.formerSchoolTeacher,
            formerInstitutionName: formData.formerSchoolTeacher ? formData.formerInstitutionName || undefined : undefined,
            formerInstitutionYears: formData.formerSchoolTeacher && formData.formerInstitutionYears !== "" ? Number(formData.formerInstitutionYears) : undefined,
          }, token);
          await refreshProfile(true);
        } catch (err: any) {
          const msg = err?.message || 'Failed to save credentials';
          toast.error(msg);
          return;
        }
      }
    }
    setCurrentStep(s => Math.min(s + 1, wizardSteps.length - 1));
  };

  const handleWizardBack = () => setCurrentStep(s => Math.max(s - 1, 0));

  const renderStepIndicator = () => (
    <div className="flex items-start w-full">
      {wizardSteps.map((step, idx) => {
        const isCompleted = stepCompleted[idx];
        const isActive    = idx === currentStep;
        return (
          <div key={step.id} className="flex items-start flex-1 min-w-0">
            <div className="flex flex-col items-center gap-1.5 flex-shrink-0">
              <button
                type="button"
                onClick={() => setCurrentStep(idx)}
                className={cn(
                  "w-9 h-9 rounded-full flex items-center justify-center border-2 transition-all duration-200 cursor-pointer",
                  isCompleted && !isActive && "bg-primary border-primary text-primary-foreground hover:bg-primary/80",
                  isActive    && isCompleted && "bg-primary border-primary text-primary-foreground shadow-[0_0_0_4px_hsl(var(--primary)/0.15)]",
                  isActive    && !isCompleted && "bg-background border-primary text-primary shadow-[0_0_0_4px_hsl(var(--primary)/0.12)]",
                  !isCompleted && !isActive && "bg-background border-border text-muted-foreground hover:border-primary/50 hover:text-primary/70"
                )}
              >
                {isCompleted
                  ? <Check className="w-4 h-4" />
                  : <step.icon className="w-4 h-4" />
                }
              </button>
              <div className="flex flex-col items-center hidden sm:flex">
                <span className={cn(
                  "text-xs font-medium text-center leading-tight max-w-[76px] truncate",
                  isActive ? "text-primary" : isCompleted ? "text-foreground" : "text-muted-foreground"
                )}>
                  {step.label}
                </span>
                {isCompleted && !isActive && (
                  <span className="text-xs text-primary/60 font-medium mt-0.5">Saved</span>
                )}
                {step.optional && !isCompleted && !isActive && (
                  <span className="text-xs text-muted-foreground/70 font-medium mt-0.5">Optional</span>
                )}
              </div>
            </div>
            {idx < wizardSteps.length - 1 && (
              <div className={cn(
                "flex-1 h-0.5 mt-[18px] mx-1 transition-colors duration-300",
                stepCompleted[idx] ? "bg-primary" : "bg-border"
              )} />
            )}
          </div>
        );
      })}
    </div>
  );

  const renderWizard = () => {
    const step = wizardSteps[currentStep];

    // State B: active profile exists but no draft in progress — view-only mode.
    // Exception: editingSection is set without a draft when the active version
    // is missing required basics (handle/location) — the user must edit the
    // About section first, and saving it creates the draft.
    const isViewOnly = hasActiveVersion && !hasDraft && editingSection !== step.id;

    // State B (view-only) or PENDING_REVIEW draft — show read-only view content
    const stepContent = (isViewOnly || isDraftReadOnly)
      ? [viewAbout, viewDescription, viewQualification, viewPhoto, viewVideo][currentStep]()
      : [renderAbout, renderDescription, viewQualification, renderPhoto, renderVideo][currentStep]();

    // Save handler for current step (steps 0 & 1 have text forms; 2-4 save inline)
    const saveHandlers: Record<number, (() => Promise<boolean | void>) | null> = {
      0: handleSaveAbout,
      1: handleSaveDescription,
      2: handleSaveCredentials,
      3: null,
      4: null,
    };
    const currentSaveHandler = saveHandlers[currentStep];

    // Disable Save when there are no unsaved changes
    const isSaveDisabled = (() => {
      // Always disabled if the step's required fields are not valid
      if (currentStep === 0 && !isAboutValid) return true;
      if (currentStep === 1 && !isDescriptionValid) return true;

      if (!teacherProfile) return false;
      if (currentStep === 0) {
        const currentSubjects = JSON.stringify(formData.subjectCombinations.map(c => ({ subjectId: c.subject, depthLevel: c.depthLevel })));
        const savedSubjects = JSON.stringify((teacherProfile.subjects || []).map((s: any) => {
          const sid = s.subjectId || s.subject?.id;
          const exp = ((teacherProfile as any).subjectExpertise ?? []).find((se: any) => se.subjectId === sid);
          return { subjectId: sid, depthLevel: exp?.depthLevel ?? '' };
        }));
        return formData.handle.trim() === (handle || '') &&
          JSON.stringify(formData.teachingModes) === JSON.stringify(teacherProfile.teachingModes || []) &&
          currentSubjects === savedSubjects &&
          locationValue?.placeId === (teacherProfile.placeId || '');
      }
      if (currentStep === 1) {
        const normalizeThreshold = (achievement: string, threshold: string | null | undefined) =>
          THRESHOLD_REQUIRED_ACHIEVEMENTS.has(achievement) ? (threshold || "") : "";
        const disabledFormStories = formData.examSuccessStories
          .filter(s => s.achievement && s.timeframe && s.studentCount !== "")
          .map(s => ({ studentCount: Number(s.studentCount), achievement: s.achievement, threshold: normalizeThreshold(s.achievement, s.threshold), subjectExamId: s.subjectExamId || "", timeframe: s.timeframe }));
        const disabledSavedStories = ((teacherProfile as any).examSuccessStories ?? [])
          .filter((s: any) => s.achievement && s.timeframe && s.studentCount !== "")
          .map((s: any) => ({ studentCount: Number(s.studentCount), achievement: s.achievement, threshold: normalizeThreshold(s.achievement, s.threshold), subjectExamId: s.subjectExamId ?? "", timeframe: s.timeframe }));
        return formData.bio === (teacherProfile.professionalBio || '') &&
          formData.teachingApproach.join(",") === (teacherProfile.teachingApproach || '') &&
          JSON.stringify(disabledFormStories) === JSON.stringify(disabledSavedStories);
      }
      if (currentStep === 2) {
        const tp = teacherProfile as any;
        return (
          (formData.highestQualification || "") === (tp.highestQualification ?? "") &&
          (formData.highestQualificationInstitution || "") === (tp.highestQualificationInstitution ?? "") &&
          (formData.teachingQualification || "") === (tp.teachingQualification ?? "") &&
          (formData.yearsTeachingExperience || "") === (tp.yearsTeachingExperience ?? "") &&
          formData.formerSchoolTeacher === (tp.formerSchoolTeacher ?? false) &&
          (formData.formerInstitutionName || "") === (tp.formerInstitutionName ?? "") &&
          (formData.formerInstitutionYears === "" ? null : Number(formData.formerInstitutionYears)) === (tp.formerInstitutionYears ?? null)
        );
      }
      return false;
    })();

    // Completion progress for required sections (About, Bio, Photo, Video)
    const requiredCompleted = [stepCompleted[0], stepCompleted[1], stepCompleted[2], stepCompleted[3], stepCompleted[4]].filter(Boolean).length;
    const requiredTotal = 5;
    const completionPct = Math.round((requiredCompleted / requiredTotal) * 100);

    return (
      <div className="flex flex-col gap-4">

        {/* ── State banners (above card) ── */}
        {hasDraft && !isDraftReadOnly && !isDraftRejected && (
          <div
            className={cn(
              "rounded-xl px-4 py-3 flex flex-col sm:flex-row sm:items-center justify-between gap-3",
              hasActiveVersion ? "bg-primary/5" : "bg-red-50",
            )}
          >
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3">
                <FileEdit className={cn("w-4 h-4 shrink-0", hasActiveVersion ? "text-primary" : "text-red-600")} />
                <p className={cn("text-sm", hasActiveVersion ? "text-primary" : "font-semibold text-red-700")}>
                  {hasActiveVersion ? "You have unpublished changes." : "Complete your profile and submit for review."}
                </p>
              </div>
              {!canSubmitForReview && incompleteSteps.length > 0 && (
                <div className="flex flex-wrap items-center gap-1.5 mt-2 sm:ml-7">
                  <span className="text-xs text-slate-600">To submit, complete:</span>
                  {incompleteSteps.map(s => (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => setCurrentStep(s.index)}
                      className="text-xs font-medium px-2 py-0.5 rounded-full bg-white text-slate-700 shadow-sm hover:shadow hover:text-primary transition-all cursor-pointer"
                      title={`Go to the ${s.label} step`}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {hasActiveVersion && <DiscardDraftDialog onConfirm={handleDiscardDraft} />}
              <Button
                size="sm"
                onClick={handleSubmitClick}
                disabled={!canSubmitForReview}
                title={!canSubmitForReview ? `Complete ${incompleteSteps.map(s => s.label).join(', ')} to submit` : undefined}
                className="gap-1.5 bg-[hsl(210,60%,28%)] hover:bg-[hsl(210,60%,22%)] disabled:opacity-50"
              >
                <CheckCircle className="w-3.5 h-3.5" />
                {isDraftRejected ? "Resubmit for Review" : "Submit for Review"}
              </Button>
            </div>
          </div>
        )}
        {isDraftReadOnly && (
          <div className="rounded-xl bg-amber-50 px-4 py-3 flex items-start gap-3">
            <ShieldCheck className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-medium text-amber-800">Profile is under review</p>
              <p className="text-xs text-amber-700 mt-0.5">Changes are locked. Usually takes {REVIEW_TIME_LABEL}.</p>
              <Button variant="outline" size="sm" className="mt-2 text-xs h-7" onClick={handleWithdrawDraft}>
                Withdraw from review
              </Button>
            </div>
          </div>
        )}
        {isDraftRejected && (
          <div className="rounded-xl bg-destructive/5 px-4 py-3 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <AlertCircle className="w-4 h-4 text-destructive shrink-0" />
              <p className="text-sm text-destructive">Review the admin feedback below and make the necessary changes.</p>
            </div>
          </div>
        )}


        {/* ── Single unified card (+ contextual help at xl) ── */}
        <div className="flex items-start gap-6">
        <div className="flex-1 min-w-0 rounded-2xl bg-white shadow-md shadow-slate-200/60">

          {/* Step indicator row — edit icon pinned to right when view-only */}
          <div className="px-4 sm:px-6 pt-5 pb-4 border-b border-slate-100 flex items-start gap-2">
            <div className="flex-1">{renderStepIndicator()}</div>
            {isViewOnly && (
              <button
                onClick={() => handleEditWithDraftCreation(step.id)}
                className="mt-0.5 inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold text-[hsl(210,60%,38%)] bg-[hsl(210,60%,38%)]/8 hover:bg-[hsl(210,60%,38%)]/15 border border-[hsl(210,60%,38%)]/20 hover:border-[hsl(210,60%,38%)]/40 transition-all shrink-0"
                title="Edit this section"
              >
                <Pencil className="w-3.5 h-3.5" />
                Edit
              </button>
            )}
          </div>

          {/* Admin comment for current section */}
          {isDraftRejected && (() => {
            const comment = getAdminComment(step.sectionType);
            return comment ? (
              <div className="mx-4 sm:mx-6 mt-4 rounded-lg bg-destructive/5 p-3 flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-destructive shrink-0 mt-0.5" />
                <div>
                  <p className="text-xs font-medium text-destructive">Admin Feedback</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{comment}</p>
                </div>
              </div>
            ) : null;
          })()}

          {/* Section content */}
          <div key={currentStep} className="px-4 sm:px-6 py-4 sm:py-6 animate-in fade-in-0 duration-200">
            {stepContent}
          </div>

          {/* Footer: Prev / Save / Next / Submit */}
          <div className="px-4 sm:px-6 pb-4 sm:pb-6 pt-3 border-t border-slate-100">
            {/* Mobile: stack vertically; desktop: side by side */}
            <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-2">
              {/* Left: Previous */}
              <Button
                variant="outline"
                onClick={handleWizardBack}
                disabled={currentStep === 0}
                className="gap-2 sm:w-auto w-full"
              >
                <ChevronLeft className="w-4 h-4" /> Previous
              </Button>

              {/* Right: Save + Next/Submit */}
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                {!isViewOnly && !isDraftReadOnly && currentSaveHandler && (
                  <Button
                    variant="outline"
                    onClick={currentSaveHandler}
                    disabled={isSaveDisabled}
                    className="gap-2 sm:w-auto w-full"
                  >
                    <Check className="w-4 h-4" /> Save only
                  </Button>
                )}

                {currentStep < wizardSteps.length - 1 ? (
                  <Button
                    onClick={handleWizardNext}
                    disabled={
                      (currentStep === 0 && !isAboutValid) ||
                      (currentStep === 1 && !isDescriptionValid) ||
                      (currentStep === 2 && !isCredentialsValid) ||
                      (currentStep === 3 && !stepCompleted[3])
                    }
                    className="gap-2 bg-[hsl(210,60%,28%)] hover:bg-[hsl(210,60%,22%)] disabled:opacity-50 sm:w-auto w-full"
                  >
                    Next <ChevronRight className="w-4 h-4" />
                  </Button>
                ) : (
                  // Last step (Intro Video) — mirror the "Submit for Review" banner button here
                  // so it's reachable from the sticky footer without scrolling up past the video UI.
                  hasDraft && !isDraftReadOnly && !isDraftRejected && (
                    <Button
                      onClick={handleSubmitClick}
                      disabled={!canSubmitForReview}
                      title={!canSubmitForReview ? `Complete ${incompleteSteps.map(s => s.label).join(', ')} to submit` : undefined}
                      className="gap-2 bg-[hsl(210,60%,28%)] hover:bg-[hsl(210,60%,22%)] disabled:opacity-50 sm:w-auto w-full"
                    >
                      <CheckCircle className="w-4 h-4" /> Submit for Review
                    </Button>
                  )
                )}
              </div>
            </div>
          </div>
        </div>

        </div>

      </div>
    );
  };

  const hasDraftChanges = (): boolean => {
    const active = profileResponse?.profile?.activeVersion?.data;
    const draft = profileResponse?.profile?.draftVersion?.data;
    if (!active || !draft) return true; // first-time teacher, always allow

    // Extract S3 key from signed URL (path before query params, last segment)
    const s3Key = (url: string | null | undefined) => {
      if (!url) return null;
      const path = url.split('?')[0];
      const segments = path.split('/');
      return segments[segments.length - 1] || path;
    };

    // Normalise nullable arrays to a stable JSON string for comparison
    const arr = (v: unknown) => JSON.stringify(v ?? []);
    // Normalise nullable scalar to null for comparison
    const val = (v: unknown) => v ?? null;

    // Basic info
    const basicChanged =
      active.fullName !== draft.fullName ||
      active.phone !== draft.phone ||
      active.placeId !== draft.placeId ||
      active.city !== draft.city ||
      active.state !== draft.state ||
      active.country !== draft.country ||
      arr(active.teachingModes) !== arr(draft.teachingModes) ||
      arr(active.teachingAudience) !== arr(draft.teachingAudience) ||
      arr(active.curriculumSystems) !== arr(draft.curriculumSystems) ||
      arr(active.instructionLanguages) !== arr(draft.instructionLanguages) ||
      val(active.otherInstructionLanguage) !== val(draft.otherInstructionLanguage) ||
      arr(active.subjects) !== arr(draft.subjects);

    // Description
    const descChanged =
      active.professionalBio !== draft.professionalBio ||
      active.teachingApproach !== draft.teachingApproach ||
      arr(active.subjectExpertise) !== arr(draft.subjectExpertise) ||
      arr(active.examSuccessStories) !== arr(draft.examSuccessStories);

    // Education / credentials
    const eduChanged =
      val(active.highestQualification) !== val(draft.highestQualification) ||
      val(active.otherHighestQualification) !== val(draft.otherHighestQualification) ||
      val(active.highestQualificationInstitution) !== val(draft.highestQualificationInstitution) ||
      val(active.teachingQualification) !== val(draft.teachingQualification) ||
      val(active.otherTeachingQualification) !== val(draft.otherTeachingQualification) ||
      val(active.yearsTeachingExperience) !== val(draft.yearsTeachingExperience) ||
      val(active.formerSchoolTeacher) !== val(draft.formerSchoolTeacher) ||
      val(active.formerInstitutionName) !== val(draft.formerInstitutionName) ||
      val(active.formerInstitutionYears) !== val(draft.formerInstitutionYears);

    // Media
    const imageChanged = s3Key(active.profileImage) !== s3Key(draft.profileImage);
    const videoChanged = (active.introVideo?.videoId ?? null) !== (draft.introVideo?.videoId ?? null);

    return basicChanged || descChanged || eduChanged || imageChanged || videoChanged;
  };

  const handleSubmitClick = () => {
    if (editingSection) {
      toast.warning('You have unsaved changes. Please save or cancel your current edits first.');
      return;
    }
    // Refresh profile first to pick up media changes, then check in handleConfirmSubmit
    refreshProfile(true).then(() => {
      // hasDraftChanges will use the updated profileResponse after re-render
      // We show the dialog and check again in handleConfirmSubmit
      setShowSubmitDialog(true);
    });
  };

  const handleConfirmSubmit = async () => {
    if (hasActiveVersion && !hasDraftChanges()) {
      setShowSubmitDialog(false);
      toast.warning('No changes detected. Please make changes before submitting for review.');
      return;
    }
    setShowSubmitDialog(false);
    await handleFinish();
  };

  const renderSubmitDialog = () => (
    <Dialog open={showSubmitDialog} onOpenChange={(open) => { setShowSubmitDialog(open); if (!open) setSubmitError(null); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2.5">
            <span className="w-9 h-9 rounded-full bg-emerald-100 flex items-center justify-center shrink-0">
              <CheckCircle className="w-5 h-5 text-emerald-600" />
            </span>
            Ready to submit?
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-1">
          <p className="text-sm text-muted-foreground">
            Your profile will be sent to our admin team for review. Once submitted:
          </p>
          <ul className="space-y-2 text-sm text-muted-foreground">
            <li className="flex items-start gap-2">
              <CheckCircle className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
              <span>You won't be able to make further changes until the review is complete.</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
              <span>The review usually takes {REVIEW_TIME_LABEL}.</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />
              <span>You'll be notified if any changes are requested before your profile goes live.</span>
            </li>
          </ul>
        </div>
        {submitError && (
          <div className="flex items-start gap-2.5 rounded-lg bg-destructive/10 px-4 py-3">
            <AlertCircle className="w-4 h-4 text-destructive shrink-0 mt-0.5" />
            <p className="text-sm text-destructive font-medium">{submitError}</p>
          </div>
        )}
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => setShowSubmitDialog(false)}>Cancel</Button>
          <Button onClick={handleConfirmSubmit} className="gap-2">
            <CheckCircle className="w-4 h-4" /> Submit for Review
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  const renderNextStepDialog = () => (
    <Dialog open={showNextStepDialog} onOpenChange={setShowNextStepDialog}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2.5">
            <span className="w-9 h-9 rounded-full bg-emerald-100 flex items-center justify-center shrink-0">
              <CheckCircle className="w-5 h-5 text-emerald-600" />
            </span>
            {OFFERING_NUDGE_COPY.modalTitle}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-1">
          <DialogDescription className="text-sm text-muted-foreground">
            {OFFERING_NUDGE_COPY.modalIntro}
          </DialogDescription>
          <div className="rounded-xl border border-[#DCDDEE] bg-[#EEEFF9] p-4 space-y-3">
            <p className="text-sm font-semibold text-[#262A7A]">{OFFERING_NUDGE_COPY.sectionHeading}</p>
            <ul className="space-y-2 text-sm text-[#5A5E80]">
              {OFFERING_NUDGE_COPY.reasons.map((reason) => (
                <li key={reason} className="flex items-start gap-2">
                  <CheckCircle className="w-4 h-4 text-[#0E8A78] shrink-0 mt-0.5" />
                  <span>{reason}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button variant="outline" onClick={() => { setShowNextStepDialog(false); navigate("/teacher-dashboard"); }}>
            {OFFERING_NUDGE_COPY.secondaryCta}
          </Button>
          <Button
            onClick={() => { setShowNextStepDialog(false); navigate("/create-offering"); }}
            className="gap-2 bg-[#262A7A] hover:bg-[#363A96]"
          >
            <ArrowRight className="w-4 h-4" /> {OFFERING_NUDGE_COPY.primaryCta}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  // Helper: in State B, clicking Edit silently creates a draft first, then opens the section
  const handleEditWithDraftCreation = async (sectionId: string) => {
    if (!hasDraft) {
      try {
        // May redirect to the About section when the active version is missing
        // required basics (handle/location) — saving About then creates the draft.
        const overrideSection = await handleCreateDraft();
        if (overrideSection) {
          const idx = wizardSteps.findIndex(s => s.id === overrideSection);
          if (idx >= 0) setCurrentStep(idx);
          setEditingSection(overrideSection);
          return;
        }
      } catch {
        return; // handleCreateDraft already toasts the error
      }
    }
    setEditingSection(sectionId);
  };

  return (
    <div className="min-h-screen bg-white teacher-theme">
      <SidebarProvider>
        <DashboardTopNav role="teacher" />
        <div className="min-h-screen flex w-full pt-14">
          <TeacherSidebar
            teacherName={currentUser?.name || "Your Name"}
            teacherAvatar={
              photoPreview
              || teacherProfile?.profileImage
              || ""
            }
            subjects={sidebarSubjects}
          />

          <div className="flex-1 flex flex-col min-w-0">
            {/* ── Page header ── */}
            <div className="px-4 sm:px-6 pt-5 pb-4 border-b border-slate-100 shrink-0 flex items-center justify-between gap-4">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h1 className="text-2xl font-bold text-slate-900">My Profile</h1>
                  {hasActiveVersion && (
                    <span className="inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" /> Live
                    </span>
                  )}
                </div>
                {hasActiveVersion ? (
                  handle ? (
                    <a
                      href={`${window.location.origin}/t/${handle}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 min-w-0 text-xs text-[hsl(210,60%,38%)] hover:text-[hsl(210,60%,25%)] hover:underline mt-0.5 transition-colors"
                    >
                      <Globe className="w-3 h-3 shrink-0" />
                      <span className="truncate">bookmycoaching.com/t/{handle}</span>
                      <ExternalLink className="w-2.5 h-2.5 shrink-0 opacity-60" />
                    </a>
                  ) : (
                    <p className="text-xs text-slate-400 mt-0.5">Your profile is live on the marketplace.</p>
                  )
                ) : (
                  <p className="text-xs text-slate-500 mt-0.5">Complete your profile and submit for review.</p>
                )}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {hasActiveVersion && handle && (
                  <Button
                    variant="outline"
                    size="icon"
                    className="text-slate-600 shrink-0"
                    title="Share profile"
                    onClick={() => {
                      const url = `${window.location.origin}/t/${handle}`;
                      if (navigator.share) {
                        navigator.share({ title: "My teaching profile", url }).catch(() => {});
                      } else {
                        navigator.clipboard.writeText(url).then(() => toast.success("Profile URL copied!"));
                      }
                    }}
                  >
                    <Share2 className="w-4 h-4" />
                  </Button>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-1.5 shrink-0"
                  onClick={() => {
                    const handle = (teacherProfile as any)?.handle;
                    if (handle) {
                      window.open(`/t/${handle}`, "_blank", "noopener,noreferrer");
                    }
                  }}
                >
                  <Eye className="w-3.5 h-3.5" />
                  Preview
                </Button>
              </div>
            </div>

            <main className="flex-1 overflow-auto bg-slate-50">
              <div className="max-w-6xl px-4 sm:px-6 lg:px-8 py-6 sm:py-8 space-y-5">
                {/* All states use the wizard layout */}
                {renderWizard()}
              </div>
            </main>
          </div>
        </div>
      </SidebarProvider>
      {renderSubmitDialog()}
      {renderNextStepDialog()}

    </div>
  );
}
